import { useState, useRef, useEffect } from "react";
import React from "react";
import { useSelector } from "react-redux";
import { createPortal } from "react-dom";
import {
  CheckSquare,
  FileText,
  Package,
  Settings,
  ChevronDown,
  Hash,
  Building2,
  X,
  Check,
  AlertTriangle,
  Tag,
  Filter,
  Search,
  Plus,
  Pencil,
  Trash2,
  Globe,
  MapPin,
  GitMerge,
  TriangleAlert,
  Users,
  Library,
  Pill,
  Copy,
  ChevronRight,
  Scale,
  ShieldCheck,
  Lock,
  Stamp,
  Activity,
  TrendingUp,
  Clock,
  ArrowUpRight,
  Eye,
  BoxSelect,
  CircleAlert,
  CircleCheck,
  CalendarClock,
  Printer,
  Smartphone,
  Timer,
  ArrowRight,
  DollarSign,
  PackageX,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  getCompanyProductRulesByCompanyId,
  addCompanyProductRule,
  editCompanyProductRule,
  searchProductDrug,
  getProductRuleAudit,
  removeCompanyProductRule,
  getMonthlyConcession,
  getHistoricalConcession,
  addConcession,
} from "@/dispatchers/titanTabDispatchers";
import { ProductRulesEnum } from "@/enum/ProductRulesEnum";

type ProductRuleCategory =
  | "concessions"
  | "equalisation"
  | "specials"
  | "appliances"
  | "pack-optimisation";

type ProductRuleAction =
  | "forceDispensing"
  | "blockClaiming"
  | "clinicalWarning"
  | "labellingWarning"
  | "handoutAlert";

const PRODUCT_RULE_ACTION_LABELS: Record<ProductRuleAction, string> = {
  forceDispensing: "Force Dispensing",
  blockClaiming: "Block Claiming",
  clinicalWarning: "Show Warning on Clinical Check",
  labellingWarning: "Show Warning at Labelling",
  handoutAlert: "Show Alert on TITAN Mobile at Handout",
};

const PRODUCT_RULE_ACTION_DESCRIPTIONS: Record<ProductRuleAction, string> = {
  forceDispensing:
    "Forces the dispenser to dispense the specified item only by showing it on the picking sheet and blocking labelling of any other item.",
  blockClaiming:
    "Puts the item into a claims queue which requires another user with access to approve before it gets claimed.",
  clinicalWarning:
    "Shows a warning to the pharmacist at clinical check to flag this item for review.",
  labellingWarning:
    "Shows a warning to the user every time the selected drug is labelled.",
  handoutAlert:
    "Shows a custom message to the patient at the point of handing out the medication via TITAN Mobile.",
};

const PRODUCT_RULE_ACTION_ICONS: Record<ProductRuleAction, React.ReactNode> = {
  forceDispensing: <Pill className="w-3.5 h-3.5" />,
  blockClaiming: <Lock className="w-3.5 h-3.5" />,
  clinicalWarning: <CircleAlert className="w-3.5 h-3.5" />,
  labellingWarning: <Printer className="w-3.5 h-3.5" />,
  handoutAlert: <Smartphone className="w-3.5 h-3.5" />,
};

const PRODUCT_RULE_ACTION_COLORS: Record<
  ProductRuleAction,
  { bg: string; text: string; bgLight: string; textLight: string }
> = {
  forceDispensing: {
    bg: "bg-blue-500/15",
    text: "text-blue-400",
    bgLight: "bg-blue-100",
    textLight: "text-blue-700",
  },
  blockClaiming: {
    bg: "bg-amber-500/15",
    text: "text-amber-400",
    bgLight: "bg-amber-100",
    textLight: "text-amber-700",
  },
  clinicalWarning: {
    bg: "bg-orange-500/15",
    text: "text-orange-400",
    bgLight: "bg-orange-100",
    textLight: "text-orange-700",
  },
  labellingWarning: {
    bg: "bg-rose-500/15",
    text: "text-rose-400",
    bgLight: "bg-rose-100",
    textLight: "text-rose-700",
  },
  handoutAlert: {
    bg: "bg-violet-500/15",
    text: "text-violet-400",
    bgLight: "bg-violet-100",
    textLight: "text-violet-700",
  },
};

interface ProductRuleItem {
  id: string;
  prescribedItemId?: string;
  name: string;
  drugTariffPrice: string;
  pack: string;
  manufacturer?: string;
  actions: ProductRuleAction[];
  enforcedProduct?: string;
  enforcedPack?: string;
  enforcedProductName?: string;
  expiryDate?: string;
  clinicalWarningText?: string;
  labellingWarningText?: string;
  handoutAlertText?: string;
  concessionPrice?: string;
  costPrice?: string;
  dtCategoryId?: string;
  dtCategoryName?: string;
  assignedStores?: string[];
}

const PRODUCT_RULE_CATEGORIES: {
  id: ProductRuleCategory;
  label: string;
  description: string;
  icon: React.ReactNode;
  defaultActions: ProductRuleAction[];
}[] = [
  {
    id: "concessions",
    label: "Concessions",
    description: "Products with NCSO concession pricing",
    icon: <Hash className="w-4 h-4" />,
    defaultActions: ["forceDispensing"],
  },
  {
    id: "equalisation",
    label: "Equalisation Deals",
    description: "Products under equalisation deal agreements",
    icon: <Scale className="w-4 h-4" />,
    defaultActions: ["forceDispensing"],
  },
  {
    id: "specials",
    label: "Specials",
    description: "Unlicensed specials requiring endorsement verification",
    icon: <AlertTriangle className="w-4 h-4" />,
    defaultActions: ["blockClaiming"],
  },
  {
    id: "appliances",
    label: "Appliances",
    description: "Appliance products with specific dispensing rules",
    icon: <Package className="w-4 h-4" />,
    defaultActions: ["forceDispensing"],
  },
  {
    id: "pack-optimisation",
    label: "Pack Optimisation",
    description: "Optimise pack sizes to reduce waste and improve margins",
    icon: <BoxSelect className="w-4 h-4" />,
    defaultActions: ["forceDispensing"],
  },
];

const categoryProducts: Record<ProductRuleCategory, ProductRuleItem[]> = {
  concessions: [
    {
      id: "CON-001",
      name: "Sertraline 100mg Tablets",
      drugTariffPrice: "£3.80",
      pack: "28 tabs",
      actions: ["forceDispensing"],
      concessionPrice: "£7.40",
      costPrice: "£2.15",
    },
    {
      id: "CON-002",
      name: "Fluoxetine 20mg Capsules",
      drugTariffPrice: "£1.54",
      pack: "30 caps",
      actions: ["forceDispensing"],
      concessionPrice: "£4.89",
      costPrice: "£1.20",
    },
    {
      id: "CON-003",
      name: "Lansoprazole 30mg Capsules",
      drugTariffPrice: "£2.96",
      pack: "28 caps",
      actions: ["forceDispensing"],
      concessionPrice: "£6.12",
      costPrice: "£3.45",
    },
    {
      id: "CON-004",
      name: "Candesartan 8mg Tablets",
      drugTariffPrice: "£2.12",
      pack: "28 tabs",
      actions: [],
      concessionPrice: "£3.95",
      costPrice: "£2.80",
    },
    {
      id: "CON-005",
      name: "Mirtazapine 30mg Tablets",
      drugTariffPrice: "£1.69",
      pack: "28 tabs",
      actions: ["forceDispensing"],
      concessionPrice: "£5.20",
      costPrice: "£1.05",
    },
    {
      id: "CON-006",
      name: "Pregabalin 150mg Capsules",
      drugTariffPrice: "£4.26",
      pack: "56 caps",
      actions: ["forceDispensing"],
      concessionPrice: "£8.50",
      costPrice: "£5.10",
    },
  ],
  equalisation: [
    {
      id: "EQ-001",
      name: "Atorvastatin 20mg Tablets",
      drugTariffPrice: "£1.03",
      pack: "28 tabs",
      actions: ["forceDispensing"],
    },
    {
      id: "EQ-002",
      name: "Ramipril 5mg Capsules",
      drugTariffPrice: "£0.89",
      pack: "28 caps",
      actions: ["forceDispensing"],
    },
    {
      id: "EQ-003",
      name: "Amlodipine 5mg Tablets",
      drugTariffPrice: "£0.56",
      pack: "28 tabs",
      actions: ["forceDispensing"],
    },
    {
      id: "EQ-004",
      name: "Omeprazole 20mg Capsules",
      drugTariffPrice: "£1.17",
      pack: "28 caps",
      actions: ["forceDispensing"],
    },
    {
      id: "EQ-005",
      name: "Bisoprolol 5mg Tablets",
      drugTariffPrice: "£0.94",
      pack: "28 tabs",
      actions: ["forceDispensing"],
    },
  ],
  specials: [
    {
      id: "SP-001",
      name: "Hydrocortisone 1mg/ml Oral Suspension",
      drugTariffPrice: "£120.00",
      pack: "100ml",
      actions: ["blockClaiming"],
    },
    {
      id: "SP-002",
      name: "Omeprazole 2mg/ml Oral Suspension",
      drugTariffPrice: "£95.50",
      pack: "150ml",
      actions: ["blockClaiming"],
    },
    {
      id: "SP-003",
      name: "Levothyroxine 25mcg/5ml Oral Solution",
      drugTariffPrice: "£145.00",
      pack: "100ml",
      actions: ["blockClaiming"],
    },
    {
      id: "SP-004",
      name: "Tacrolimus 0.5mg/ml Oral Suspension",
      drugTariffPrice: "£210.00",
      pack: "100ml",
      actions: ["blockClaiming"],
    },
  ],
  appliances: [
    {
      id: "APP-001",
      name: "Salts Confidence Natural Drainable Pouch",
      drugTariffPrice: "£5.24",
      pack: "10 pouches",
      actions: ["forceDispensing"],
    },
    {
      id: "APP-002",
      name: "ConvaTec Esteem Synergy Closed Pouch",
      drugTariffPrice: "£6.18",
      pack: "30 pouches",
      actions: ["forceDispensing"],
    },
    {
      id: "APP-003",
      name: "Coloplast SenSura Mio 1-Piece Drainable",
      drugTariffPrice: "£5.87",
      pack: "10 pouches",
      actions: ["forceDispensing"],
    },
    {
      id: "APP-004",
      name: "Hollister Moderma Flex Closed Pouch",
      drugTariffPrice: "£4.95",
      pack: "30 pouches",
      actions: [],
    },
    {
      id: "APP-005",
      name: "Dansac NovaLife 1 Convex Drainable",
      drugTariffPrice: "£6.42",
      pack: "10 pouches",
      actions: ["forceDispensing"],
    },
  ],
  "pack-optimisation": [
    {
      id: "PO-001",
      name: "Metformin 500mg Tablets",
      drugTariffPrice: "£0.88",
      pack: "84 tabs",
      actions: ["forceDispensing"],
    },
    {
      id: "PO-002",
      name: "Omeprazole 20mg Capsules",
      drugTariffPrice: "£1.17",
      pack: "28 caps",
      actions: ["forceDispensing"],
    },
    {
      id: "PO-003",
      name: "Simvastatin 40mg Tablets",
      drugTariffPrice: "£0.72",
      pack: "28 tabs",
      actions: ["forceDispensing"],
    },
    {
      id: "PO-004",
      name: "Amlodipine 10mg Tablets",
      drugTariffPrice: "£0.62",
      pack: "28 tabs",
      actions: [],
    },
    {
      id: "PO-005",
      name: "Lisinopril 20mg Tablets",
      drugTariffPrice: "£0.95",
      pack: "28 tabs",
      actions: ["forceDispensing"],
    },
    {
      id: "PO-006",
      name: "Levothyroxine 50mcg Tablets",
      drugTariffPrice: "£0.54",
      pack: "28 tabs",
      actions: ["forceDispensing"],
    },
  ],
};

// ── DISPENSABLE PRODUCTS DATABASE (for Force Dispensing search) ──────────────
interface DispensableProduct {
  name: string;
  manufacturer: string;
  packs: string[];
}

const DISPENSABLE_PRODUCTS: DispensableProduct[] = [
  {
    name: "Sertraline 100mg Tablets",
    manufacturer: "Accord",
    packs: ["28 tabs", "56 tabs"],
  },
  {
    name: "Sertraline 100mg Tablets",
    manufacturer: "Teva",
    packs: ["28 tabs", "56 tabs"],
  },
  {
    name: "Sertraline 100mg Tablets",
    manufacturer: "Zentiva",
    packs: ["28 tabs"],
  },
  {
    name: "Sertraline 50mg Tablets",
    manufacturer: "Accord",
    packs: ["28 tabs", "56 tabs"],
  },
  { name: "Sertraline 50mg Tablets", manufacturer: "Teva", packs: ["28 tabs"] },
  {
    name: "Fluoxetine 20mg Capsules",
    manufacturer: "Accord",
    packs: ["30 caps", "60 caps"],
  },
  {
    name: "Fluoxetine 20mg Capsules",
    manufacturer: "Teva",
    packs: ["30 caps"],
  },
  {
    name: "Fluoxetine 20mg Capsules",
    manufacturer: "Zentiva",
    packs: ["30 caps", "60 caps"],
  },
  {
    name: "Lansoprazole 30mg Capsules",
    manufacturer: "Accord",
    packs: ["28 caps", "56 caps"],
  },
  {
    name: "Lansoprazole 30mg Capsules",
    manufacturer: "Teva",
    packs: ["28 caps"],
  },
  {
    name: "Lansoprazole 15mg Capsules",
    manufacturer: "Accord",
    packs: ["28 caps"],
  },
  {
    name: "Candesartan 8mg Tablets",
    manufacturer: "Accord",
    packs: ["28 tabs"],
  },
  { name: "Candesartan 8mg Tablets", manufacturer: "Teva", packs: ["28 tabs"] },
  {
    name: "Mirtazapine 30mg Tablets",
    manufacturer: "Accord",
    packs: ["28 tabs"],
  },
  {
    name: "Mirtazapine 30mg Tablets",
    manufacturer: "Teva",
    packs: ["28 tabs", "56 tabs"],
  },
  {
    name: "Mirtazapine 15mg Tablets",
    manufacturer: "Accord",
    packs: ["28 tabs"],
  },
  {
    name: "Pregabalin 150mg Capsules",
    manufacturer: "Accord",
    packs: ["56 caps"],
  },
  {
    name: "Pregabalin 150mg Capsules",
    manufacturer: "Teva",
    packs: ["56 caps", "84 caps"],
  },
  {
    name: "Pregabalin 75mg Capsules",
    manufacturer: "Accord",
    packs: ["56 caps"],
  },
  {
    name: "Atorvastatin 20mg Tablets",
    manufacturer: "Accord",
    packs: ["28 tabs"],
  },
  {
    name: "Atorvastatin 20mg Tablets",
    manufacturer: "Teva",
    packs: ["28 tabs"],
  },
  {
    name: "Atorvastatin 40mg Tablets",
    manufacturer: "Accord",
    packs: ["28 tabs"],
  },
  { name: "Ramipril 5mg Capsules", manufacturer: "Accord", packs: ["28 caps"] },
  { name: "Ramipril 5mg Capsules", manufacturer: "Teva", packs: ["28 caps"] },
  {
    name: "Ramipril 10mg Capsules",
    manufacturer: "Accord",
    packs: ["28 caps"],
  },
  {
    name: "Amlodipine 5mg Tablets",
    manufacturer: "Accord",
    packs: ["28 tabs"],
  },
  { name: "Amlodipine 5mg Tablets", manufacturer: "Teva", packs: ["28 tabs"] },
  {
    name: "Amlodipine 10mg Tablets",
    manufacturer: "Accord",
    packs: ["28 tabs"],
  },
  { name: "Amlodipine 10mg Tablets", manufacturer: "Teva", packs: ["28 tabs"] },
  {
    name: "Omeprazole 20mg Capsules",
    manufacturer: "Accord",
    packs: ["28 caps", "56 caps"],
  },
  {
    name: "Omeprazole 20mg Capsules",
    manufacturer: "Teva",
    packs: ["28 caps"],
  },
  {
    name: "Omeprazole 10mg Capsules",
    manufacturer: "Accord",
    packs: ["28 caps"],
  },
  {
    name: "Bisoprolol 5mg Tablets",
    manufacturer: "Accord",
    packs: ["28 tabs"],
  },
  { name: "Bisoprolol 5mg Tablets", manufacturer: "Teva", packs: ["28 tabs"] },
  {
    name: "Metformin 500mg Tablets",
    manufacturer: "Accord",
    packs: ["28 tabs", "84 tabs"],
  },
  {
    name: "Metformin 500mg Tablets",
    manufacturer: "Teva",
    packs: ["28 tabs", "84 tabs"],
  },
  {
    name: "Metformin 500mg Tablets",
    manufacturer: "Actavis",
    packs: ["84 tabs"],
  },
  {
    name: "Simvastatin 40mg Tablets",
    manufacturer: "Accord",
    packs: ["28 tabs"],
  },
  {
    name: "Simvastatin 40mg Tablets",
    manufacturer: "Teva",
    packs: ["28 tabs"],
  },
  {
    name: "Lisinopril 20mg Tablets",
    manufacturer: "Accord",
    packs: ["28 tabs"],
  },
  { name: "Lisinopril 20mg Tablets", manufacturer: "Teva", packs: ["28 tabs"] },
  {
    name: "Levothyroxine 50mcg Tablets",
    manufacturer: "Accord",
    packs: ["28 tabs", "56 tabs"],
  },
  {
    name: "Levothyroxine 50mcg Tablets",
    manufacturer: "Teva",
    packs: ["28 tabs"],
  },
  {
    name: "Levothyroxine 100mcg Tablets",
    manufacturer: "Accord",
    packs: ["28 tabs"],
  },
];

type CategoryScope = { mode: "all" | "selected"; branches: string[] };

type ConcessionRisk = "high" | "medium" | "low";
type ConcessionMonitorTab = "current" | "historical" | "risk";

interface ConcessionItem {
  id: string;
  drug: string;
  packSize: string;
  concessionPrice: string;
  dtPrice: string;
  difference: string;
  previousPrice?: string;
  rolledOver?: boolean;
}

interface ConcessionHistoryItem {
  id: string;
  drug: string;
  packSize: string;
  month: string;
  concessionPrice: string;
  dtPrice: string;
  previousPrice?: string;
}

interface ConcessionRiskItem {
  id: string;
  drug: string;
  packSize: string;
  dtPrice: string;
  timesOnConcession: number;
  lastOnConcession: string;
  risk: ConcessionRisk;
  currentlyOn: boolean;
}

const CURRENT_CONCESSIONS: ConcessionItem[] = [
  {
    id: "CC-001",
    drug: "Mirtazapine 15mg tablets",
    packSize: "28",
    concessionPrice: "£0.97",
    dtPrice: "£0.72",
    difference: "+£0.25",
    rolledOver: false,
  },
  {
    id: "CC-002",
    drug: "Ramipril 5mg capsules",
    packSize: "28",
    concessionPrice: "£0.79",
    dtPrice: "£0.58",
    difference: "+£0.21",
    rolledOver: false,
  },
  {
    id: "CC-003",
    drug: "Ramipril 10mg capsules",
    packSize: "28",
    concessionPrice: "£0.85",
    dtPrice: "£0.62",
    difference: "+£0.23",
    rolledOver: false,
  },
  {
    id: "CC-004",
    drug: "Pregabalin 225mg capsules",
    packSize: "56",
    concessionPrice: "£2.12",
    dtPrice: "£1.45",
    difference: "+£0.67",
    rolledOver: false,
  },
  {
    id: "CC-005",
    drug: "Duloxetine 60mg gastro-resistant capsules",
    packSize: "28",
    concessionPrice: "£1.84",
    dtPrice: "£1.20",
    difference: "+£0.64",
    rolledOver: false,
  },
  {
    id: "CC-006",
    drug: "Lansoprazole 30mg orodispersible tablets",
    packSize: "28",
    concessionPrice: "£3.35",
    dtPrice: "£2.96",
    difference: "+£0.39",
    rolledOver: false,
  },
  {
    id: "CC-007",
    drug: "Venlafaxine 37.5mg tablets",
    packSize: "56",
    concessionPrice: "£2.60",
    dtPrice: "£1.85",
    difference: "+£0.75",
    rolledOver: true,
  },
  {
    id: "CC-008",
    drug: "Quinine sulfate 300mg tablets",
    packSize: "28",
    concessionPrice: "£2.01",
    dtPrice: "£1.58",
    difference: "+£0.43",
    rolledOver: true,
  },
  {
    id: "CC-009",
    drug: "Amlodipine 2.5mg tablets",
    packSize: "28",
    concessionPrice: "£3.88",
    dtPrice: "£0.85",
    difference: "+£3.03",
    rolledOver: false,
  },
  {
    id: "CC-010",
    drug: "Bisoprolol 1.25mg tablets",
    packSize: "28",
    concessionPrice: "£1.99",
    dtPrice: "£0.94",
    difference: "+£1.05",
    rolledOver: false,
  },
  {
    id: "CC-011",
    drug: "Sertraline 100mg tablets",
    packSize: "28",
    concessionPrice: "£1.78",
    dtPrice: "£0.95",
    difference: "+£0.83",
    rolledOver: false,
  },
  {
    id: "CC-012",
    drug: "Hydroxychloroquine 200mg tablets",
    packSize: "60",
    concessionPrice: "£3.21",
    dtPrice: "£2.17",
    difference: "+£1.04",
    rolledOver: false,
  },
  {
    id: "CC-013",
    drug: "Carbocisteine 375mg capsules",
    packSize: "120",
    concessionPrice: "£3.18",
    dtPrice: "£2.60",
    difference: "+£0.58",
    rolledOver: false,
  },
  {
    id: "CC-014",
    drug: "Propranolol 40mg tablets",
    packSize: "28",
    concessionPrice: "£0.75",
    dtPrice: "£0.48",
    difference: "+£0.27",
    rolledOver: false,
  },
  {
    id: "CC-015",
    drug: "Topiramate 100mg tablets",
    packSize: "60",
    concessionPrice: "£1.95",
    dtPrice: "£1.32",
    difference: "+£0.63",
    rolledOver: false,
  },
];

const HISTORICAL_CONCESSIONS: ConcessionHistoryItem[] = [
  {
    id: "HC-001",
    drug: "Captopril 12.5mg tablets",
    packSize: "56",
    month: "Feb 2026",
    concessionPrice: "£29.04",
    dtPrice: "£15.50",
    previousPrice: "£27.95",
  },
  {
    id: "HC-002",
    drug: "Captopril 50mg tablets",
    packSize: "56",
    month: "Feb 2026",
    concessionPrice: "£39.00",
    dtPrice: "£21.00",
    previousPrice: "£26.95",
  },
  {
    id: "HC-003",
    drug: "Clarithromycin 125mg/5ml oral suspension",
    packSize: "70",
    month: "Feb 2026",
    concessionPrice: "£11.10",
    dtPrice: "£6.80",
    previousPrice: "£9.59",
  },
  {
    id: "HC-004",
    drug: "Lofepramine 70mg tablets",
    packSize: "56",
    month: "Feb 2026",
    concessionPrice: "£24.86",
    dtPrice: "£12.40",
    previousPrice: "£23.89",
  },
  {
    id: "HC-005",
    drug: "Trimethoprim 100mg tablets",
    packSize: "28",
    month: "Feb 2026",
    concessionPrice: "£4.07",
    dtPrice: "£2.15",
    previousPrice: "£3.70",
  },
  {
    id: "HC-006",
    drug: "Fluoxetine 20mg capsules",
    packSize: "30",
    month: "Jan 2026",
    concessionPrice: "£1.82",
    dtPrice: "£0.91",
    previousPrice: undefined,
  },
  {
    id: "HC-007",
    drug: "Sertraline 100mg tablets",
    packSize: "28",
    month: "Jan 2026",
    concessionPrice: "£1.65",
    dtPrice: "£0.95",
    previousPrice: undefined,
  },
  {
    id: "HC-008",
    drug: "Pregabalin 150mg capsules",
    packSize: "56",
    month: "Dec 2025",
    concessionPrice: "£3.90",
    dtPrice: "£2.80",
    previousPrice: undefined,
  },
  {
    id: "HC-009",
    drug: "Mirtazapine 30mg tablets",
    packSize: "28",
    month: "Dec 2025",
    concessionPrice: "£1.52",
    dtPrice: "£0.85",
    previousPrice: undefined,
  },
  {
    id: "HC-010",
    drug: "Lansoprazole 30mg capsules",
    packSize: "28",
    month: "Nov 2025",
    concessionPrice: "£3.50",
    dtPrice: "£2.96",
    previousPrice: undefined,
  },
  {
    id: "HC-011",
    drug: "Amlodipine 5mg tablets",
    packSize: "28",
    month: "Nov 2025",
    concessionPrice: "£1.10",
    dtPrice: "£0.56",
    previousPrice: undefined,
  },
  {
    id: "HC-012",
    drug: "Ramipril 5mg capsules",
    packSize: "28",
    month: "Oct 2025",
    concessionPrice: "£0.72",
    dtPrice: "£0.58",
    previousPrice: undefined,
  },
  {
    id: "HC-013",
    drug: "Candesartan 8mg tablets",
    packSize: "28",
    month: "Oct 2025",
    concessionPrice: "£2.45",
    dtPrice: "£1.50",
    previousPrice: undefined,
  },
  {
    id: "HC-014",
    drug: "Bisoprolol 5mg tablets",
    packSize: "28",
    month: "Sep 2025",
    concessionPrice: "£1.15",
    dtPrice: "£0.94",
    previousPrice: undefined,
  },
  {
    id: "HC-015",
    drug: "Venlafaxine 75mg tablets",
    packSize: "56",
    month: "Sep 2025",
    concessionPrice: "£7.20",
    dtPrice: "£4.50",
    previousPrice: undefined,
  },
];

const CONCESSION_RISK_ITEMS: ConcessionRiskItem[] = [
  {
    id: "CR-001",
    drug: "Sertraline 100mg tablets",
    packSize: "28",
    dtPrice: "£0.95",
    timesOnConcession: 8,
    lastOnConcession: "Mar 2026",
    risk: "high",
    currentlyOn: true,
  },
  {
    id: "CR-002",
    drug: "Fluoxetine 20mg capsules",
    packSize: "30",
    dtPrice: "£0.91",
    timesOnConcession: 7,
    lastOnConcession: "Jan 2026",
    risk: "high",
    currentlyOn: false,
  },
  {
    id: "CR-003",
    drug: "Mirtazapine 15mg tablets",
    packSize: "28",
    dtPrice: "£0.72",
    timesOnConcession: 6,
    lastOnConcession: "Mar 2026",
    risk: "high",
    currentlyOn: true,
  },
  {
    id: "CR-004",
    drug: "Ramipril 5mg capsules",
    packSize: "28",
    dtPrice: "£0.58",
    timesOnConcession: 6,
    lastOnConcession: "Mar 2026",
    risk: "high",
    currentlyOn: true,
  },
  {
    id: "CR-005",
    drug: "Pregabalin 150mg capsules",
    packSize: "56",
    dtPrice: "£2.80",
    timesOnConcession: 5,
    lastOnConcession: "Dec 2025",
    risk: "high",
    currentlyOn: false,
  },
  {
    id: "CR-006",
    drug: "Lansoprazole 30mg capsules",
    packSize: "28",
    dtPrice: "£2.96",
    timesOnConcession: 5,
    lastOnConcession: "Nov 2025",
    risk: "high",
    currentlyOn: false,
  },
  {
    id: "CR-007",
    drug: "Amlodipine 5mg tablets",
    packSize: "28",
    dtPrice: "£0.56",
    timesOnConcession: 4,
    lastOnConcession: "Nov 2025",
    risk: "medium",
    currentlyOn: false,
  },
  {
    id: "CR-008",
    drug: "Venlafaxine 37.5mg tablets",
    packSize: "56",
    dtPrice: "£1.85",
    timesOnConcession: 4,
    lastOnConcession: "Mar 2026",
    risk: "medium",
    currentlyOn: true,
  },
  {
    id: "CR-009",
    drug: "Bisoprolol 1.25mg tablets",
    packSize: "28",
    dtPrice: "£0.94",
    timesOnConcession: 3,
    lastOnConcession: "Mar 2026",
    risk: "medium",
    currentlyOn: true,
  },
  {
    id: "CR-010",
    drug: "Candesartan 8mg tablets",
    packSize: "28",
    dtPrice: "£1.50",
    timesOnConcession: 3,
    lastOnConcession: "Oct 2025",
    risk: "medium",
    currentlyOn: false,
  },
  {
    id: "CR-011",
    drug: "Duloxetine 60mg gastro-resistant capsules",
    packSize: "28",
    dtPrice: "£1.20",
    timesOnConcession: 2,
    lastOnConcession: "Mar 2026",
    risk: "low",
    currentlyOn: true,
  },
  {
    id: "CR-012",
    drug: "Captopril 12.5mg tablets",
    packSize: "56",
    dtPrice: "£15.50",
    timesOnConcession: 2,
    lastOnConcession: "Feb 2026",
    risk: "low",
    currentlyOn: false,
  },
  {
    id: "CR-013",
    drug: "Propranolol 40mg tablets",
    packSize: "28",
    dtPrice: "£0.48",
    timesOnConcession: 2,
    lastOnConcession: "Mar 2026",
    risk: "low",
    currentlyOn: true,
  },
  {
    id: "CR-014",
    drug: "Hydroxychloroquine 200mg tablets",
    packSize: "60",
    dtPrice: "£2.17",
    timesOnConcession: 1,
    lastOnConcession: "Mar 2026",
    risk: "low",
    currentlyOn: true,
  },
  {
    id: "CR-015",
    drug: "Topiramate 100mg tablets",
    packSize: "60",
    dtPrice: "£1.32",
    timesOnConcession: 1,
    lastOnConcession: "Mar 2026",
    risk: "low",
    currentlyOn: true,
  },
];

const RISK_STYLES: Record<
  ConcessionRisk,
  {
    bg: string;
    text: string;
    bgLight: string;
    textLight: string;
    label: string;
  }
> = {
  high: {
    bg: "bg-red-500/15",
    text: "text-red-400",
    bgLight: "bg-red-100",
    textLight: "text-red-700",
    label: "High",
  },
  medium: {
    bg: "bg-amber-500/15",
    text: "text-amber-400",
    bgLight: "bg-amber-100",
    textLight: "text-amber-700",
    label: "Medium",
  },
  low: {
    bg: "bg-emerald-500/15",
    text: "text-emerald-400",
    bgLight: "bg-emerald-100",
    textLight: "text-emerald-700",
    label: "Low",
  },
};

interface Pharmacy {
  pharmacyID: string;
  companyID: string;
  pharmacyName: string;
  ods: string;
}

const BRANCH_COLORS = [
  "#7c3aed",
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
  "#ec4899",
  "#f97316",
  "#14b8a6",
];

const ENUM_TO_ACTION: Record<number, ProductRuleAction> = {
  3: "forceDispensing",
  2: "blockClaiming",
  4: "clinicalWarning",
  5: "labellingWarning",
  6: "handoutAlert",
};

const CATEGORY_TO_RULE_TYPE: Record<ProductRuleCategory, number> = {
  concessions: 1,
  equalisation: 2,
  specials: 3,
  appliances: 4,
  "pack-optimisation": 5,
};

const RULE_TYPE_TO_CATEGORY: Record<number, ProductRuleCategory> = {
  1: "concessions",
  2: "equalisation",
  3: "specials",
  4: "appliances",
  5: "pack-optimisation",
};

const DRUG_TARIFF_CATEGORIES: {
  id: string;
  name: string;
  items: { name: string; packs: string[] }[];
}[] = [
  {
    id: "dt-part-ix-a",
    name: "Part IXA - Appliances",
    items: [
      { name: "Catheter Maintenance Solution", packs: ["100ml", "50ml"] },
      { name: "Colostomy Bags - Drainable", packs: ["10 bags", "30 bags"] },
      { name: "Elastic Hosiery - Below Knee", packs: ["1 pair", "2 pairs"] },
      { name: "Finger Stall", packs: ["1 stall"] },
      { name: "Incontinence Sheath", packs: ["30 sheaths"] },
      { name: "Ileostomy Bags - Drainable", packs: ["10 bags", "30 bags"] },
      { name: "Leg Bag - Urinary", packs: ["10 bags"] },
      { name: "Lymphoedema Garments", packs: ["1 garment"] },
      { name: "Peak Flow Meter", packs: ["1 device"] },
      { name: "Stoma Cap", packs: ["30 caps"] },
      { name: "Truss - Inguinal", packs: ["1 truss"] },
      { name: "Wound Drainage Pouch", packs: ["10 pouches"] },
    ],
  },
  {
    id: "dt-part-ix-b",
    name: "Part IXB - Incontinence Appliances",
    items: [
      { name: "Absorbent Bed Pad", packs: ["25 pads", "100 pads"] },
      { name: "Absorbent Pant - Light", packs: ["14 pants", "28 pants"] },
      { name: "Absorbent Pant - Moderate", packs: ["14 pants", "28 pants"] },
      { name: "Absorbent Pant - Heavy", packs: ["14 pants"] },
      { name: "Male Urinal - Body Worn", packs: ["1 device"] },
      { name: "Night Drainage Bag", packs: ["10 bags"] },
    ],
  },
  {
    id: "dt-part-ix-c",
    name: "Part IXC - Stoma Appliances",
    items: [
      { name: "Colostomy Bag - Closed", packs: ["30 bags"] },
      { name: "Colostomy Bag - Drainable", packs: ["10 bags", "30 bags"] },
      { name: "Ileostomy Bag - Closed", packs: ["30 bags"] },
      { name: "Ileostomy Bag - Drainable", packs: ["10 bags", "30 bags"] },
      { name: "Urostomy Bag", packs: ["10 bags", "30 bags"] },
      { name: "Stoma Flange", packs: ["5 flanges", "10 flanges"] },
      { name: "Stoma Adhesive Remover", packs: ["1 spray", "30 wipes"] },
      { name: "Stoma Skin Protector", packs: ["1 spray", "30 wipes"] },
    ],
  },
  {
    id: "dt-part-ix-r",
    name: "Part IXR - Chemical Reagents",
    items: [
      { name: "Blood Glucose Test Strips", packs: ["50 strips", "100 strips"] },
      { name: "Blood Ketone Test Strips", packs: ["10 strips"] },
      { name: "Urine Glucose Test Strips", packs: ["50 strips"] },
      { name: "Urine Protein Test Strips", packs: ["50 strips"] },
      { name: "Lancets", packs: ["100 lancets", "200 lancets"] },
    ],
  },
  {
    id: "dt-part-viii-a",
    name: "Part VIIIA - Wound Management",
    items: [
      { name: "Absorbent Cotton", packs: ["25g", "100g", "500g"] },
      {
        name: "Adhesive Dressing - Standard",
        packs: ["10 dressings", "50 dressings"],
      },
      { name: "Alginate Dressing", packs: ["10 dressings"] },
      { name: "Foam Dressing - Non-Adhesive", packs: ["10 dressings"] },
      { name: "Hydrocolloid Dressing", packs: ["5 dressings", "10 dressings"] },
      { name: "Permeable Adhesive Tape", packs: ["1 roll"] },
      { name: "Sterile Dressing Pack", packs: ["1 pack", "10 packs"] },
      { name: "Surgical Suture", packs: ["12 sutures"] },
      { name: "Tubular Bandage", packs: ["1 roll"] },
      { name: "Zinc Paste Bandage", packs: ["1 bandage"] },
    ],
  },
  {
    id: "dt-part-xv",
    name: "Part XV - Borderline Substances",
    items: [
      { name: "Ensure Plus Advance", packs: ["24 bottles"] },
      { name: "Fortisip Compact Protein", packs: ["24 bottles"] },
      { name: "Fresubin Energy Fibre", packs: ["24 bottles"] },
      { name: "Complan Shake", packs: ["4 sachets", "24 sachets"] },
      { name: "Nutrison Standard", packs: ["500ml", "1000ml"] },
      { name: "Peptamen", packs: ["500ml"] },
    ],
  },
];

export default function ProductRulesTab({
  isDarkMode,
  selectedBranches,
}: {
  isDarkMode: boolean;
  selectedBranches: string[];
}) {
  const dark = isDarkMode;
  // Subscribe to the three slices this tab reads. useTitanApp() registers ~30
  // useSelector subscriptions, so any unrelated dispatch re-rendered this
  // 6,000-line component and rebuilt every derived list below.
  const appState = useSelector((state: any) => state.app);
  const titanTabState = useSelector((state: any) => state.titanTab);
  const pharmacyServiceState = useSelector((state: any) => state.pharmacyService);
  const { companyId, currentCompanyId } = (appState as any) || {};
  const effectiveCompanyId = currentCompanyId || companyId;

  const pharmacies: Pharmacy[] = (pharmacyServiceState?.allPharmacies ||
    []) as Pharmacy[];
  const apiProductRules: any[] = titanTabState?.companyProductRules || [];
  const isTitanTabLoading: boolean = titanTabState?.isTitanTabLoading || false;
  const monthlyConcessions: any[] = titanTabState?.monthlyConcessions || [];
  const isMonthlyConcessionLoading: boolean =
    titanTabState?.isMonthlyConcessionLoading || false;
  const historicalConcessions: any[] =
    titanTabState?.historicalConcessions || [];
  const isHistoricalConcessionLoading: boolean =
    titanTabState?.isHistoricalConcessionLoading || false;

  const now = new Date();
  const currentMonthLabel = now.toLocaleString("default", {
    month: "short",
    year: "numeric",
  });
  const currentMonthParam = `${now.getFullYear()}-${String(
    now.getMonth() + 1,
  ).padStart(2, "0")}-01`;

  // Initial load: fetch the company's product rules on mount / when the company changes.
  // (The mutate handlers below also refetch, but without this the list never populates.)
  useEffect(() => {
    if (effectiveCompanyId) {
      getCompanyProductRulesByCompanyId(effectiveCompanyId);
    }
  }, [effectiveCompanyId]);

  const mapApiItemToProductRuleItem = (
    item: any,
  ): ProductRuleItem & { _category: ProductRuleCategory } => {
    const activeActions: ProductRuleAction[] = (item.productRules || [])
      .filter((r: any) => r.isActive)
      .map((r: any) => ENUM_TO_ACTION[r.productRuleSubType])
      .filter(Boolean);

    const ruleByEnum = (enumVal: number) =>
      (item.productRules || []).find(
        (r: any) => r.productRuleSubType === enumVal && r.isActive,
      );

    // Force dispensing: extract enforced product from productRuleDispense
    const fdRule = ruleByEnum(ProductRulesEnum.ForceDispensing);
    const fdDispense = fdRule?.productRuleDispense?.[0];
    const enforcedProduct = fdDispense
      ? `${fdDispense.productName || item.productName} - ${
          fdDispense.manufacturerName
        }`
      : undefined;
    const enforcedPack =
      fdDispense?.packSize != null
        ? String(Math.round(fdDispense.packSize))
        : undefined;
    const enforcedProductName = fdDispense?.productName || undefined;

    return {
      id: item._id,
      prescribedItemId: item.prescribedItemId,
      name: item.productName,
      pack: item.packSize || "Any",
      manufacturer: item.manufacturer || "Any",
      drugTariffPrice:
        item.priceDt != null ? `£${parseFloat(item.priceDt).toFixed(2)}` : "—",
      concessionPrice:
        item.priceConcession != null
          ? `£${parseFloat(item.priceConcession).toFixed(2)}`
          : undefined,
      costPrice:
        item.priceCost != null
          ? `£${parseFloat(item.priceCost).toFixed(2)}`
          : undefined,
      actions: activeActions,
      enforcedProduct,
      enforcedPack,
      enforcedProductName,
      expiryDate: item.expireDate ? item.expireDate.split("T")[0] : undefined,
      clinicalWarningText:
        ruleByEnum(ProductRulesEnum.ClinicalCheckWarning)?.message || undefined,
      labellingWarningText:
        ruleByEnum(ProductRulesEnum.LabellingWarning)?.message || undefined,
      handoutAlertText:
        ruleByEnum(ProductRulesEnum.MobileHandoutAlert)?.message || undefined,
      assignedStores: (item.pharmacies || [])
        .filter((p: any) => p.isActive)
        .map((p: any) => p.pharmacyID),
      _category: RULE_TYPE_TO_CATEGORY[item.productRuleType] || "concessions",
    };
  };

  const [activeCategory, setActiveCategory] = useState<ProductRuleCategory>(
    "concessions",
  );
  const [selectedCategories, setSelectedCategories] = useState<
    Set<ProductRuleCategory>
  >(new Set(["concessions"]));
  const [products, setProducts] = useState<
    Record<ProductRuleCategory, ProductRuleItem[]>
  >(categoryProducts);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryScopes, setCategoryScopes] = useState<
    Record<ProductRuleCategory, CategoryScope>
  >({
    concessions: { mode: "all", branches: [] },
    equalisation: { mode: "all", branches: [] },
    specials: { mode: "all", branches: [] },
    appliances: { mode: "all", branches: [] },
    "pack-optimisation": { mode: "all", branches: [] },
  });
  const [scopeDropdownOpen, setScopeDropdownOpen] = useState(false);
  const [concessionTab, setConcessionTab] = useState<
    "rules" | ConcessionMonitorTab
  >("rules");
  const [monitorSearch, setMonitorSearch] = useState("");

  // Per-store action overrides: storeOverrides[category][productId][pharmacyId] = actions[]
  const [storeOverrides, setStoreOverrides] = useState<
    Record<
      ProductRuleCategory,
      Record<string, Record<string, ProductRuleAction[]>>
    >
  >(() => {
    const init: Record<
      string,
      Record<string, Record<string, ProductRuleAction[]>>
    > = {};
    for (const cat of PRODUCT_RULE_CATEGORIES) {
      init[cat.id] = {};
      for (const p of categoryProducts[cat.id]) {
        init[cat.id][p.id] = {};
      }
    }
    return init as Record<
      ProductRuleCategory,
      Record<string, Record<string, ProductRuleAction[]>>
    >;
  });

  // Expanded product rows — default: expand first product with active rules to show current site
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(() => {
    const firstActive = categoryProducts["concessions"].find(
      (p) => p.actions.length > 0,
    );
    return firstActive ? new Set([firstActive.id]) : new Set();
  });
  const toggleExpanded = (productId: string) => {
    setExpandedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const currentScope = categoryScopes[activeCategory];

  const setScopeMode = (mode: "all" | "selected") => {
    setCategoryScopes((prev) => ({
      ...prev,
      [activeCategory]: {
        mode,
        branches: mode === "all" ? [] : prev[activeCategory].branches,
      },
    }));
    if (mode === "selected") setScopeDropdownOpen(true);
  };

  const toggleScopeBranch = (branchId: string) => {
    setCategoryScopes((prev) => {
      const curr = prev[activeCategory];
      const has = curr.branches.includes(branchId);
      return {
        ...prev,
        [activeCategory]: {
          mode: "selected",
          branches: has
            ? curr.branches.filter((b) => b !== branchId)
            : [...curr.branches, branchId],
        },
      };
    });
  };

  const removeScopeBranch = (branchId: string) => {
    setCategoryScopes((prev) => {
      const newBranches = prev[activeCategory].branches.filter(
        (b) => b !== branchId,
      );
      return {
        ...prev,
        [activeCategory]: {
          mode: newBranches.length === 0 ? "all" : "selected",
          branches: newBranches,
        },
      };
    });
  };

  const [sortField, setSortField] = useState<
    "name" | "pack" | "dtPrice" | "rules" | "expires" | null
  >(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const toggleSort = (
    field: "name" | "pack" | "dtPrice" | "rules" | "expires",
  ) => {
    if (sortField === field) {
      if (sortDir === "asc") setSortDir("desc");
      else {
        setSortField(null);
        setSortDir("asc");
      }
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const category = PRODUCT_RULE_CATEGORIES.find(
    (c) => c.id === activeCategory,
  )!;
  const items = products[activeCategory];
  const multiCategory = selectedCategories.size > 1;
  const mergedItems: (ProductRuleItem & {
    _category: ProductRuleCategory;
  })[] = React.useMemo(() => {
    const result: (ProductRuleItem & { _category: ProductRuleCategory })[] = [];
    for (const catId of Array.from(selectedCategories)) {
      for (const p of products[catId]) {
        result.push({ ...p, _category: catId });
      }
    }
    return result;
  }, [selectedCategories, products]);
  const apiMappedItems = React.useMemo(
    () =>
      apiProductRules
        .map(mapApiItemToProductRuleItem)
        .filter((item) => item._category === activeCategory),
    [apiProductRules, activeCategory],
  );

  const displayItems = apiMappedItems;
  const filteredAndSortedItems = React.useMemo(() => {
    let result = searchQuery
      ? displayItems.filter((p) =>
          p.name.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : [...displayItems];
    if (sortField) {
      result = [...result].sort((a, b) => {
        let cmp = 0;
        switch (sortField) {
          case "name":
            cmp = a.name.localeCompare(b.name);
            break;
          case "pack":
            cmp = a.pack.localeCompare(b.pack);
            break;
          case "dtPrice": {
            const pa =
              parseFloat((a.drugTariffPrice || "").replace("£", "")) || 0;
            const pb =
              parseFloat((b.drugTariffPrice || "").replace("£", "")) || 0;
            cmp = pa - pb;
            break;
          }
          case "rules":
            cmp = a.actions.length - b.actions.length;
            break;
          case "expires":
            cmp = (a.expiryDate || "").localeCompare(b.expiryDate || "");
            break;
        }
        return sortDir === "desc" ? -cmp : cmp;
      });
    }
    return result;
  }, [displayItems, searchQuery, sortField, sortDir]);
  const filteredItems = filteredAndSortedItems;
  const toggleAction = (productId: string, action: ProductRuleAction) => {
    const cat = getCategoryForProduct(productId);
    setProducts((prev) => ({
      ...prev,
      [cat]: prev[cat].map((p) => {
        if (p.id !== productId) return p;
        const isRemoving = p.actions.includes(action);
        const newActions = isRemoving
          ? p.actions.filter((a) => a !== action)
          : [...p.actions, action];
        const updates: Partial<ProductRuleItem> = { actions: newActions };
        if (action === "forceDispensing" && isRemoving) {
          updates.enforcedProduct = undefined;
          updates.enforcedPack = undefined;
        }
        return { ...p, ...updates };
      }),
    }));
    // Sync to all store overrides
    setStoreOverrides((prev) => {
      const catOverrides = { ...prev[cat] };
      const prodOverrides = { ...(catOverrides[productId] || {}) };
      const currentActions =
        products[cat].find((p) => p.id === productId)?.actions || [];
      const isRemoving = currentActions.includes(action);
      for (const p of pharmacies) {
        const branchActions = prodOverrides[p.pharmacyID] || [
          ...currentActions,
        ];
        prodOverrides[p.pharmacyID] = isRemoving
          ? branchActions.filter((a) => a !== action)
          : branchActions.includes(action)
          ? branchActions
          : [...branchActions, action];
      }
      catOverrides[productId] = prodOverrides;
      return { ...prev, [cat]: catOverrides };
    });
  };

  const toggleStoreAction = (
    productId: string,
    branchId: string,
    action: ProductRuleAction,
  ) => {
    const cat = getCategoryForProduct(productId);
    setStoreOverrides((prev) => {
      const catOverrides = { ...prev[cat] };
      const prodOverrides = { ...(catOverrides[productId] || {}) };
      const branchActions = prodOverrides[branchId] || [];
      prodOverrides[branchId] = branchActions.includes(action)
        ? branchActions.filter((a) => a !== action)
        : [...branchActions, action];
      catOverrides[productId] = prodOverrides;
      return { ...prev, [cat]: catOverrides };
    });
  };

  const getCategoryForProduct = (productId: string): ProductRuleCategory => {
    for (const catId of PRODUCT_RULE_CATEGORIES.map((c) => c.id)) {
      if (products[catId].some((p) => p.id === productId)) return catId;
    }
    return activeCategory;
  };

  const getStoreActions = (
    productId: string,
    branchId: string,
  ): ProductRuleAction[] => {
    const cat = getCategoryForProduct(productId);
    return storeOverrides[cat]?.[productId]?.[branchId] || [];
  };

  // Per-product excluded stores
  const [excludedStores, setExcludedStores] = useState<
    Record<ProductRuleCategory, Record<string, Set<string>>>
  >(() => {
    const init: Record<string, Record<string, Set<string>>> = {};
    for (const cat of PRODUCT_RULE_CATEGORIES) init[cat.id] = {};
    return init as Record<ProductRuleCategory, Record<string, Set<string>>>;
  });

  const isStoreExcluded = (productId: string, branchId: string) => {
    const cat = getCategoryForProduct(productId);
    return excludedStores[cat]?.[productId]?.has(branchId) ?? false;
  };

  const toggleStoreExcluded = (productId: string, branchId: string) => {
    const cat = getCategoryForProduct(productId);
    setExcludedStores((prev) => {
      const catExcl = { ...prev[cat] };
      const prodSet = new Set(catExcl[productId] || []);
      if (prodSet.has(branchId)) prodSet.delete(branchId);
      else prodSet.add(branchId);
      catExcl[productId] = prodSet;
      return { ...prev, [cat]: catExcl };
    });
  };

  const [forceDispEditId, setForceDispEditId] = useState<string | null>(null);

  const updateEnforcedField = (
    productId: string,
    field: "enforcedProduct" | "enforcedPack",
    value: string,
  ) => {
    const cat = getCategoryForProduct(productId);
    setProducts((prev) => ({
      ...prev,
      [cat]: prev[cat].map((p) =>
        p.id === productId ? { ...p, [field]: value } : p,
      ),
    }));
  };

  const applyDefaultActions = () => {
    setProducts((prev) => ({
      ...prev,
      [activeCategory]: prev[activeCategory].map((p) => ({
        ...p,
        actions: [...category.defaultActions],
      })),
    }));
  };

  const clearAllActions = () => {
    setProducts((prev) => ({
      ...prev,
      [activeCategory]: prev[activeCategory].map((p) => ({
        ...p,
        actions: [],
      })),
    }));
  };

  // Stable identities — the memoised concession tables take these as props.
  const addToConcessionsList = React.useCallback((
    drug: string,
    packSize: string,
    dtPrice: string,
  ) => {
    setProducts((prev) => {
      const exists = prev.concessions.some(
        (p) => p.name.toLowerCase() === drug.toLowerCase(),
      );
      if (exists) return prev;
      const newId = `CON-${String(prev.concessions.length + 1).padStart(
        3,
        "0",
      )}`;
      return {
        ...prev,
        concessions: [
          ...prev.concessions,
          {
            id: newId,
            name: drug,
            pack: packSize,
            drugTariffPrice: dtPrice,
            actions: ["forceDispensing"],
          },
        ],
      };
    });
  }, []);

  const isOnConcessionsList = React.useCallback((drug: string) =>
    products.concessions.some(
      (p) => p.name.toLowerCase() === drug.toLowerCase(),
    ), [products.concessions]);

  const handleAddConcession = React.useCallback(async (item: any) => {
    await addConcession(item);
    getCompanyProductRulesByCompanyId(effectiveCompanyId);
  }, [effectiveCompanyId]);

  const activeCount = React.useMemo(() => displayItems.filter((p) => p.actions.length > 0).length, [displayItems]);
  const isConcessions = !multiCategory && activeCategory === "concessions";
  const showPriceColumns = isConcessions || apiMappedItems.length > 0;

  // Drug rule modal state: null = closed, { mode: 'add' } or { mode: 'edit', productId }
  const [ruleModal, setRuleModal] = useState<
    { mode: "add" } | { mode: "edit"; productId: string } | null
  >(null);

  const removeProduct = async (productId: string) => {
    const item = apiMappedItems.find((p) => p.id === productId);
    if (!item) return;
    const prescribedItemId = item.prescribedItemId || item.id;
    await removeCompanyProductRule(prescribedItemId, productId);
    getCompanyProductRulesByCompanyId(effectiveCompanyId);
  };

  const ACTION_TO_SUBTYPE: Record<ProductRuleAction, number> = {
    forceDispensing: 3,
    blockClaiming: 2,
    clinicalWarning: 4,
    labellingWarning: 5,
    handoutAlert: 6,
  };

  const ACTION_DEFAULT_MESSAGE: Record<ProductRuleAction, string> = {
    forceDispensing: "Force dispensing required for this product.",
    blockClaiming: "Block claiming enabled for this product.",
    clinicalWarning: "Clinical check warning should be shown.",
    labellingWarning: "Labelling warning should be shown.",
    handoutAlert: "Titan Mobile alert enabled.",
  };

  const saveFromModal = (data: {
    name: string;
    pack: string;
    manufacturer: string;
    actions: ProductRuleAction[];
    enforcedProduct?: string;
    enforcedPack?: string;
    enforcedProductId?: string;
    dispenseItems?: Array<{
      productId: string;
      productName: string;
      manufacturerName: string;
      packSize: number;
    }>;
    expiryDate?: string;
    clinicalWarningText?: string;
    labellingWarningText?: string;
    handoutAlertText?: string;
    existingId?: string;
    dtCategoryId?: string;
    dtCategoryName?: string;
    assignedStores?: string[];
    prescribedItemId?: string;
  }) => {
    const stores = data.assignedStores || pharmacies.map((p) => p.pharmacyID);
    if (data.existingId) {
      // Edit existing — build same payload as add, plus the rule ID
      const originalApiItem = apiProductRules.find(
        (item: any) => item._id === data.existingId,
      );
      const existingFdDispense =
        (originalApiItem?.productRules || []).find(
          (r: any) =>
            Array.isArray(r.productRuleDispense) &&
            r.productRuleDispense.length > 0,
        )?.productRuleDispense || null;

      const productRulesPayload = data.actions.map((action) => {
        const subType = ACTION_TO_SUBTYPE[action];
        const message =
          action === "clinicalWarning"
            ? data.clinicalWarningText || ACTION_DEFAULT_MESSAGE[action]
            : action === "labellingWarning"
            ? data.labellingWarningText || ACTION_DEFAULT_MESSAGE[action]
            : action === "handoutAlert"
            ? data.handoutAlertText || ACTION_DEFAULT_MESSAGE[action]
            : ACTION_DEFAULT_MESSAGE[action];
        let productRuleDispense = null;
        if (action === "forceDispensing") {
          productRuleDispense = data.dispenseItems?.length
            ? data.dispenseItems.map((d: any) => ({
                productId: d.productId,
                productName: d.productName,
                manufacturerName: d.manufacturerName,
                packSize: d.packSize,
              }))
            : existingFdDispense;
        }
        return {
          productRuleSubType: subType,
          isActive: true,
          message,
          productRuleDispense,
        };
      });

      const allPharmaciesPayload = pharmacies.map((p) => ({
        pharmacyID: p.pharmacyID,
        ods: p.ods || "",
        pharmacyName: p.pharmacyName,
        isActive: stores.includes(p.pharmacyID),
      }));

      const editPayload = {
        _id: originalApiItem?._id,
        companyID: effectiveCompanyId,
        productName: data.name,
        productRuleType: CATEGORY_TO_RULE_TYPE[activeCategory],
        packSize: data.pack,
        manufacturer: data.manufacturer,
        priceConcession: "",
        expireDate: data.expiryDate ? `${data.expiryDate}T00:00:00Z` : null,
        pharmacies: allPharmaciesPayload,
        productRules: productRulesPayload,
        createdAt: null,
        createdBy: null,
        createdByEmail: null,
        updatedAt: null,
        updatedBy: null,
        updatedByEmail: null,
        prescribedItemId:
          originalApiItem?.prescribedItemId || data.prescribedItemId || "",
        priceCost: "",
        priceDt: "",
      };

      editCompanyProductRule(editPayload).then((result: any) => {
        if (result !== null && result !== undefined && effectiveCompanyId) {
          getCompanyProductRulesByCompanyId(effectiveCompanyId);
        }
      });
    } else {
      // Add new — build payload and call API
      const productRulesPayload = data.actions.map((action) => {
        const subType = ACTION_TO_SUBTYPE[action];
        const message =
          action === "clinicalWarning"
            ? data.clinicalWarningText || ACTION_DEFAULT_MESSAGE[action]
            : action === "labellingWarning"
            ? data.labellingWarningText || ACTION_DEFAULT_MESSAGE[action]
            : action === "handoutAlert"
            ? data.handoutAlertText || ACTION_DEFAULT_MESSAGE[action]
            : ACTION_DEFAULT_MESSAGE[action];
        const productRuleDispense =
          action === "forceDispensing" && data.dispenseItems?.length
            ? data.dispenseItems.map((d: any) => ({
                productId: d.productId,
                productName: d.productName,
                manufacturerName: d.manufacturerName,
                packSize: d.packSize,
              }))
            : null;
        return {
          productRuleSubType: subType,
          isActive: true,
          message,
          productRuleDispense,
        };
      });

      const allPharmaciesPayload = pharmacies.map((p) => ({
        pharmacyID: p.pharmacyID,
        ods: p.ods || "",
        pharmacyName: p.pharmacyName,
        isActive: stores.includes(p.pharmacyID),
      }));

      const payload = {
        companyID: effectiveCompanyId,
        productName: data.name,
        productRuleType: CATEGORY_TO_RULE_TYPE[activeCategory],
        packSize: data.pack,
        manufacturer: data.manufacturer,
        priceConcession: "",
        expireDate: data.expiryDate ? `${data.expiryDate}T00:00:00Z` : null,
        pharmacies: allPharmaciesPayload,
        productRules: productRulesPayload,
        createdAt: null,
        createdBy: null,
        createdByEmail: null,
        updatedAt: null,
        updatedBy: null,
        updatedByEmail: null,
        prescribedItemId: data.prescribedItemId || "",
        priceCost: "",
        priceDt: "",
      };

      addCompanyProductRule(payload).then((result: any) => {
        if (result !== null && result !== undefined && effectiveCompanyId) {
          getCompanyProductRulesByCompanyId(effectiveCompanyId);
        }
      });
    }
    setRuleModal(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2
            className={`text-lg font-semibold ${
              dark ? "text-white" : "text-gray-900"
            }`}
          >
            Product Rules
          </h2>
          <p
            className={`text-sm mt-0.5 ${
              dark ? "text-gray-400" : "text-gray-500"
            }`}
          >
            Configure rules for products by category
          </p>
        </div>
      </div>

      {/* Category tabs — multi-select */}
      <div
        className={`flex items-center gap-4 mb-5 border-b ${
          dark ? "border-gray-700" : "border-gray-200"
        }`}
      >
        {PRODUCT_RULE_CATEGORIES.map((cat) => {
          const isSelected = selectedCategories.has(cat.id);
          const catCount = isSelected
            ? apiMappedItems.filter((p) => p.actions.length > 0).length
            : 0;
          return (
            <button
              key={cat.id}
              onClick={(e) => {
                if (e.ctrlKey || e.metaKey || e.shiftKey) {
                  // Multi-select: toggle this category
                  setSelectedCategories((prev) => {
                    const next = new Set(prev);
                    if (next.has(cat.id) && next.size > 1) {
                      next.delete(cat.id);
                    } else {
                      next.add(cat.id);
                    }
                    // Keep activeCategory in sync — set to first selected if current was deselected
                    if (!next.has(activeCategory))
                      setActiveCategory(Array.from(next)[0]);
                    return next;
                  });
                } else {
                  // Single select: select only this one
                  setActiveCategory(cat.id);
                  setSelectedCategories(new Set([cat.id]));
                  setSearchQuery("");
                  setConcessionTab("rules");
                }
              }}
              className={`flex items-center gap-1.5 pb-2 text-xs font-medium border-b-2 -mb-px transition-colors cursor-pointer whitespace-nowrap ${
                isSelected
                  ? dark
                    ? "border-gray-300 text-white"
                    : "border-gray-900 text-gray-900"
                  : `border-transparent ${
                      dark
                        ? "text-gray-400 hover:text-gray-200"
                        : "text-gray-500 hover:text-gray-700"
                    }`
              }`}
            >
              {cat.icon}
              {cat.label}
              {catCount > 0 && (
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                    isSelected
                      ? dark
                        ? "bg-gray-700 text-gray-300"
                        : "bg-gray-200 text-gray-700"
                      : dark
                      ? "bg-gray-800 text-gray-500"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {catCount}
                </span>
              )}
            </button>
          );
        })}
        {selectedCategories.size > 1 && (
          <button
            onClick={() => {
              setSelectedCategories(new Set([activeCategory]));
            }}
            className={`ml-auto pb-2 text-[10px] font-medium cursor-pointer -mb-px ${
              dark
                ? "text-gray-500 hover:text-gray-300"
                : "text-gray-400 hover:text-gray-600"
            }`}
          >
            Clear multi-select
          </button>
        )}
      </div>
      {selectedCategories.size > 1 && (
        <p
          className={`text-[11px] mb-3 -mt-3 ${
            dark ? "text-gray-500" : "text-gray-400"
          }`}
        >
          Showing {selectedCategories.size} categories · {filteredItems.length}{" "}
          item{filteredItems.length !== 1 ? "s" : ""} total. Hold Ctrl/Cmd+click
          to toggle categories.
        </p>
      )}

      {/* Concession sub-tabs: Rules / Current / Historical / Risk */}
      {isConcessions && (
        <div
          className={`flex items-center gap-4 mb-4 border-b ${
            dark ? "border-gray-700" : "border-gray-200"
          }`}
        >
          {[
            {
              id: "rules" as const,
              label: "My Rules",
              icon: <Scale className="w-3.5 h-3.5" />,
            },
            {
              id: "current" as const,
              label: `Current (${currentMonthLabel})`,
              icon: <Activity className="w-3.5 h-3.5" />,
            },
            {
              id: "historical" as const,
              label: "Historical",
              icon: <Clock className="w-3.5 h-3.5" />,
            },
            {
              id: "risk" as const,
              label: "Risk Analysis",
              icon: <TrendingUp className="w-3.5 h-3.5" />,
            },
          ].map((tab) => {
            const isActive = concessionTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setConcessionTab(tab.id);
                  setMonitorSearch("");
                  setSearchQuery("");
                  if (tab.id === "current")
                    getMonthlyConcession(currentMonthParam);
                  if (tab.id === "historical") getHistoricalConcession();
                }}
                className={`flex items-center gap-1.5 pb-2 text-xs font-medium border-b-2 -mb-px transition-colors cursor-pointer ${
                  isActive
                    ? dark
                      ? "border-gray-300 text-white"
                      : "border-gray-900 text-gray-900"
                    : `border-transparent ${
                        dark
                          ? "text-gray-400 hover:text-gray-200"
                          : "text-gray-500 hover:text-gray-700"
                      }`
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            );
          })}
        </div>
      )}

      {/* ── CONCESSION MONITOR TABS (current / historical / risk) ── */}
      {isConcessions && concessionTab !== "rules" && (
        <ConcessionMonitorContent
          dark={dark}
          monitorTab={concessionTab as ConcessionMonitorTab}
          monitorSearch={monitorSearch}
          setMonitorSearch={setMonitorSearch}
          isOnConcessionsList={isOnConcessionsList}
          addToConcessionsList={addToConcessionsList}
          onAddConcession={handleAddConcession}
          concessionsCount={apiMappedItems.length}
          monthlyConcessions={monthlyConcessions}
          isMonthlyConcessionLoading={isMonthlyConcessionLoading}
          historicalConcessions={historicalConcessions}
          isHistoricalConcessionLoading={isHistoricalConcessionLoading}
        />
      )}

      {/* ── RULES VIEW (for concessions "rules" tab or any other category) ── */}
      {(!isConcessions || concessionTab === "rules") && (
        <>
          {/* Search + Add drug */}
          <div className="flex items-center gap-2 mb-4">
            <div className="relative flex-1">
              <Search
                className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                  dark ? "text-gray-500" : "text-gray-400"
                }`}
              />
              <input
                type="text"
                placeholder={`Search ${category.label.toLowerCase()}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-9 pr-4 py-2.5 text-sm rounded-xl border outline-none transition-colors ${
                  dark
                    ? "bg-gray-900 border-gray-800 text-white placeholder:text-gray-500 focus:border-gray-600"
                    : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-gray-400"
                }`}
              />
            </div>
            <button
              onClick={() => setRuleModal({ mode: "add" })}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium rounded-xl border transition-colors cursor-pointer shrink-0 ${
                dark
                  ? "bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
              }`}
            >
              <Plus className="w-4 h-4" />
              Add Drug
            </button>
          </div>

          {/* Products table */}
          <div
            className={`rounded-xl border overflow-hidden ${
              dark ? "border-gray-800" : "border-gray-200"
            }`}
          >
            <table className="w-full text-sm">
              <thead>
                <tr className={dark ? "bg-gray-800/60" : "bg-gray-50"}>
                  {([
                    ["name", "Product"],
                    ["pack", "Pack"],
                    ["dtPrice", "DT Price"],
                  ] as const).map(([field, label]) => (
                    <th
                      key={field}
                      className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer select-none ${
                        sortField === field
                          ? dark
                            ? "text-white"
                            : "text-gray-900"
                          : dark
                          ? "text-gray-400"
                          : "text-gray-500"
                      }`}
                      onClick={() => toggleSort(field)}
                    >
                      <span className="inline-flex items-center gap-1">
                        {label}
                        {sortField === field && (
                          <ChevronDown
                            className={`w-3 h-3 transition-transform ${
                              sortDir === "desc" ? "rotate-180" : ""
                            }`}
                          />
                        )}
                      </span>
                    </th>
                  ))}
                  {showPriceColumns && (
                    <th
                      className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                        dark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Concession
                    </th>
                  )}
                  {showPriceColumns && (
                    <th
                      className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                        dark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Cost Price
                    </th>
                  )}
                  {showPriceColumns && (
                    <th
                      className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                        dark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Profit / Loss
                    </th>
                  )}
                  {([
                    ["rules", "Active Rules"],
                    ["expires", "Expires"],
                  ] as const).map(([field, label]) => (
                    <th
                      key={field}
                      className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider cursor-pointer select-none ${
                        sortField === field
                          ? dark
                            ? "text-white"
                            : "text-gray-900"
                          : dark
                          ? "text-gray-400"
                          : "text-gray-500"
                      }`}
                      onClick={() => toggleSort(field)}
                    >
                      <span className="inline-flex items-center gap-1">
                        {label}
                        {sortField === field && (
                          <ChevronDown
                            className={`w-3 h-3 transition-transform ${
                              sortDir === "desc" ? "rotate-180" : ""
                            }`}
                          />
                        )}
                      </span>
                    </th>
                  ))}
                  <th
                    className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider ${
                      dark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {isTitanTabLoading ? (
                  <tr>
                    <td
                      colSpan={showPriceColumns ? 9 : 6}
                      className="text-center py-12"
                    >
                      <div
                        className={`w-5 h-5 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-2 ${
                          dark ? "border-gray-500" : "border-gray-400"
                        }`}
                      />
                      <p
                        className={`text-sm ${
                          dark ? "text-gray-500" : "text-gray-400"
                        }`}
                      >
                        Loading product rules...
                      </p>
                    </td>
                  </tr>
                ) : filteredItems.length === 0 ? (
                  <tr>
                    <td
                      colSpan={showPriceColumns ? 9 : 6}
                      className="text-center py-12"
                    >
                      <Scale
                        className={`w-6 h-6 mx-auto mb-2 ${
                          dark ? "text-gray-600" : "text-gray-300"
                        }`}
                      />
                      <p
                        className={`text-sm ${
                          dark ? "text-gray-500" : "text-gray-400"
                        }`}
                      >
                        {searchQuery
                          ? "No matching products"
                          : "No products in this category"}
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((product, i) => {
                    const hasForceDisp = product.actions.includes(
                      "forceDispensing",
                    );
                    const isEditingForceDisp = forceDispEditId === product.id;
                    const assignedCount = product.assignedStores
                      ? product.assignedStores.length
                      : pharmacies.length;
                    return (
                      <React.Fragment key={product.id}>
                        <tr
                          className={`border-t cursor-pointer ${
                            dark
                              ? "border-gray-800 hover:bg-gray-800/40"
                              : "border-gray-100 hover:bg-gray-50"
                          } ${
                            i % 2 === 0
                              ? ""
                              : dark
                              ? "bg-gray-900/30"
                              : "bg-gray-50/30"
                          }`}
                          onClick={() =>
                            setRuleModal({
                              mode: "edit",
                              productId: product.id,
                            })
                          }
                        >
                          <td
                            className={`px-4 py-3 font-medium ${
                              dark ? "text-white" : "text-gray-900"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              {product.dtCategoryId && (
                                <Library
                                  className={`w-3.5 h-3.5 shrink-0 ${
                                    dark ? "text-teal-400" : "text-teal-600"
                                  }`}
                                />
                              )}
                              {product.name}
                              {product.dtCategoryName &&
                                (() => {
                                  const dtCat = DRUG_TARIFF_CATEGORIES.find(
                                    (c) => c.id === product.dtCategoryId,
                                  );
                                  const drugCount = dtCat?.items.length || 0;
                                  return (
                                    <span
                                      className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                                        dark
                                          ? "bg-teal-500/15 text-teal-400"
                                          : "bg-teal-100 text-teal-700"
                                      }`}
                                    >
                                      {product.dtCategoryName}
                                      {drugCount > 1
                                        ? ` · ${drugCount} drugs`
                                        : ""}
                                    </span>
                                  );
                                })()}
                              {multiCategory && (
                                <span
                                  className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                                    dark
                                      ? "bg-purple-500/15 text-purple-400"
                                      : "bg-purple-100 text-purple-700"
                                  }`}
                                >
                                  {PRODUCT_RULE_CATEGORIES.find(
                                    (c) => c.id === (product as any)._category,
                                  )?.label || ""}
                                </span>
                              )}
                              <span
                                className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${
                                  assignedCount === pharmacies.length
                                    ? dark
                                      ? "bg-emerald-500/15 text-emerald-400"
                                      : "bg-emerald-100 text-emerald-700"
                                    : dark
                                    ? "bg-gray-800 text-gray-400"
                                    : "bg-gray-100 text-gray-500"
                                }`}
                              >
                                <Building2 className="w-2.5 h-2.5" />
                                {assignedCount === pharmacies.length
                                  ? "All stores"
                                  : `${assignedCount} of ${pharmacies.length}`}
                              </span>
                            </div>
                          </td>
                          <td
                            className={`px-4 py-3 ${
                              dark ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            {product.pack}
                          </td>
                          <td
                            className={`px-4 py-3 font-mono text-xs ${
                              dark ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            {product.drugTariffPrice}
                          </td>
                          {showPriceColumns && (
                            <td
                              className={`px-4 py-3 font-mono text-xs ${
                                dark ? "text-gray-300" : "text-gray-700"
                              }`}
                            >
                              {product.concessionPrice || "—"}
                            </td>
                          )}
                          {showPriceColumns && (
                            <td
                              className={`px-4 py-3 font-mono text-xs ${
                                dark ? "text-gray-300" : "text-gray-700"
                              }`}
                            >
                              {product.costPrice || "—"}
                            </td>
                          )}
                          {showPriceColumns &&
                            (() => {
                              const dt = parseFloat(
                                (product.drugTariffPrice || "").replace(
                                  "£",
                                  "",
                                ),
                              );
                              const cost = parseFloat(
                                (product.costPrice || "").replace("£", ""),
                              );
                              if (isNaN(dt) || isNaN(cost))
                                return (
                                  <td
                                    className={`px-4 py-3 font-mono text-xs ${
                                      dark ? "text-gray-500" : "text-gray-400"
                                    }`}
                                  >
                                    —
                                  </td>
                                );
                              const diff = dt - cost;
                              const isProfit = diff >= 0;
                              return (
                                <td className="px-4 py-3">
                                  <span
                                    className={`inline-flex items-center gap-1 font-mono text-xs font-medium px-2 py-0.5 rounded-full ${
                                      isProfit
                                        ? dark
                                          ? "bg-emerald-500/15 text-emerald-400"
                                          : "bg-emerald-100 text-emerald-700"
                                        : dark
                                        ? "bg-red-500/15 text-red-400"
                                        : "bg-red-100 text-red-700"
                                    }`}
                                  >
                                    {isProfit ? "+" : ""}
                                    {diff >= 0 ? "£" : "-£"}
                                    {Math.abs(diff).toFixed(2)}
                                  </span>
                                </td>
                              );
                            })()}
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {product.actions.length === 0 ? (
                                <span
                                  className={`text-xs ${
                                    dark ? "text-gray-600" : "text-gray-400"
                                  }`}
                                >
                                  None
                                </span>
                              ) : (
                                product.actions.map((a) => {
                                  const c = PRODUCT_RULE_ACTION_COLORS[a];
                                  return (
                                    <span
                                      key={a}
                                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                        dark
                                          ? `${c.bg} ${c.text}`
                                          : `${c.bgLight} ${c.textLight}`
                                      }`}
                                    >
                                      {PRODUCT_RULE_ACTION_ICONS[a]}
                                      {PRODUCT_RULE_ACTION_LABELS[a]}
                                      {a === "forceDispensing" &&
                                        product.enforcedProduct && (
                                          <span className="opacity-75 ml-0.5">
                                            → {product.enforcedProduct}
                                            {product.enforcedPack
                                              ? ` (${product.enforcedPack})`
                                              : ""}
                                          </span>
                                        )}
                                    </span>
                                  );
                                })
                              )}
                            </div>
                          </td>
                          <td
                            className={`px-4 py-3 ${
                              dark ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            {product.expiryDate ? (
                              (() => {
                                const exp = new Date(product.expiryDate);
                                const now = new Date();
                                const daysLeft = Math.ceil(
                                  (exp.getTime() - now.getTime()) /
                                    (1000 * 60 * 60 * 24),
                                );
                                const isExpired = daysLeft < 0;
                                const isSoon = daysLeft >= 0 && daysLeft <= 14;
                                return (
                                  <div className="flex items-center gap-1.5">
                                    <CalendarClock
                                      className={`w-3 h-3 ${
                                        isExpired
                                          ? "text-red-400"
                                          : isSoon
                                          ? "text-amber-400"
                                          : dark
                                          ? "text-gray-500"
                                          : "text-gray-400"
                                      }`}
                                    />
                                    <span
                                      className={`text-xs ${
                                        isExpired
                                          ? dark
                                            ? "text-red-400"
                                            : "text-red-600"
                                          : isSoon
                                          ? dark
                                            ? "text-amber-400"
                                            : "text-amber-600"
                                          : ""
                                      }`}
                                    >
                                      {exp.toLocaleDateString("en-GB", {
                                        day: "numeric",
                                        month: "short",
                                        year: "numeric",
                                      })}
                                    </span>
                                    {isExpired && (
                                      <span
                                        className={`text-[9px] px-1 py-0.5 rounded font-medium ${
                                          dark
                                            ? "bg-red-500/20 text-red-400"
                                            : "bg-red-100 text-red-600"
                                        }`}
                                      >
                                        Expired
                                      </span>
                                    )}
                                    {isSoon && !isExpired && (
                                      <span
                                        className={`text-[9px] px-1 py-0.5 rounded font-medium ${
                                          dark
                                            ? "bg-amber-500/20 text-amber-400"
                                            : "bg-amber-100 text-amber-600"
                                        }`}
                                      >
                                        Soon
                                      </span>
                                    )}
                                  </div>
                                );
                              })()
                            ) : (
                              <span
                                className={`text-xs ${
                                  dark ? "text-gray-600" : "text-gray-400"
                                }`}
                              >
                                —
                              </span>
                            )}
                          </td>
                          <td
                            className="px-4 py-3"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() =>
                                  setRuleModal({
                                    mode: "edit",
                                    productId: product.id,
                                  })
                                }
                                title="Edit rule"
                                className={`p-1.5 rounded-lg transition-all cursor-pointer ${
                                  dark
                                    ? "text-gray-600 hover:text-gray-300 hover:bg-gray-800"
                                    : "text-gray-300 hover:text-gray-600 hover:bg-gray-100"
                                }`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => removeProduct(product.id)}
                                title="Remove from list"
                                className={`p-1.5 rounded-lg transition-all cursor-pointer ${
                                  dark
                                    ? "text-gray-600 hover:text-red-400 hover:bg-red-500/10"
                                    : "text-gray-300 hover:text-red-500 hover:bg-red-50"
                                }`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Drug Rule Modal */}
      {ruleModal &&
        createPortal(
          <DrugRuleModal
            dark={dark}
            mode={ruleModal.mode}
            existingProduct={
              ruleModal.mode === "edit"
                ? apiMappedItems.find(
                    (p) =>
                      p.id ===
                      (ruleModal as { mode: "edit"; productId: string })
                        .productId,
                  )
                : undefined
            }
            categoryLabel={category.label}
            defaultActions={category.defaultActions}
            pharmacies={pharmacies}
            onSave={saveFromModal}
            onDelete={
              ruleModal.mode === "edit"
                ? async () => {
                    await removeProduct(
                      (ruleModal as { mode: "edit"; productId: string })
                        .productId,
                    );
                    setRuleModal(null);
                  }
                : undefined
            }
            onClose={() => setRuleModal(null)}
          />,
          document.body,
        )}
    </div>
  );
}

function ConcessionMonitorContent({
  dark,
  monitorTab,
  monitorSearch,
  setMonitorSearch,
  isOnConcessionsList,
  addToConcessionsList,
  onAddConcession,
  concessionsCount,
  monthlyConcessions,
  isMonthlyConcessionLoading,
  historicalConcessions,
  isHistoricalConcessionLoading,
}: {
  dark: boolean;
  monitorTab: ConcessionMonitorTab;
  monitorSearch: string;
  setMonitorSearch: (s: string) => void;
  isOnConcessionsList: (drug: string) => boolean;
  addToConcessionsList: (
    drug: string,
    packSize: string,
    dtPrice: string,
  ) => void;
  onAddConcession: (item: any) => Promise<any>;
  concessionsCount: number;
  monthlyConcessions: any[];
  isMonthlyConcessionLoading: boolean;
  historicalConcessions: any[];
  isHistoricalConcessionLoading: boolean;
}) {
  const [detailDrug, setDetailDrug] = useState<{
    name: string;
    packSize: string;
    dtPrice: string;
  } | null>(null);

  return (
    <>
      {detailDrug && (
        <ConcessionDetailModal
          dark={dark}
          drugName={detailDrug.name}
          isOnList={isOnConcessionsList(detailDrug.name)}
          addToList={() =>
            addToConcessionsList(
              detailDrug.name,
              detailDrug.packSize,
              detailDrug.dtPrice,
            )
          }
          onClose={() => setDetailDrug(null)}
        />
      )}
      <div>
        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-4 mb-5">
          <div
            className={`px-4 py-3 rounded-xl border ${
              dark
                ? "bg-gray-900/50 border-gray-800"
                : "bg-white border-gray-200"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Activity
                className={`w-4 h-4 ${
                  dark ? "text-blue-400" : "text-blue-600"
                }`}
              />
              <span
                className={`text-xs font-medium ${
                  dark ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Current Concessions
              </span>
            </div>
            <p
              className={`text-2xl font-bold ${
                dark ? "text-white" : "text-gray-900"
              }`}
            >
              {monthlyConcessions.length}
            </p>
            <p
              className={`text-xs mt-0.5 ${
                dark ? "text-gray-500" : "text-gray-400"
              }`}
            >
              {new Date().toLocaleString("default", {
                month: "long",
                year: "numeric",
              })}
            </p>
          </div>
          <div
            className={`px-4 py-3 rounded-xl border ${
              dark
                ? "bg-gray-900/50 border-gray-800"
                : "bg-white border-gray-200"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp
                className={`w-4 h-4 ${dark ? "text-red-400" : "text-red-600"}`}
              />
              <span
                className={`text-xs font-medium ${
                  dark ? "text-gray-400" : "text-gray-500"
                }`}
              >
                High Risk Drugs
              </span>
            </div>
            <p
              className={`text-2xl font-bold ${
                dark ? "text-white" : "text-gray-900"
              }`}
            >
              {CONCESSION_RISK_ITEMS.filter((r) => r.risk === "high").length}
            </p>
            <p
              className={`text-xs mt-0.5 ${
                dark ? "text-gray-500" : "text-gray-400"
              }`}
            >
              Frequently on concessions
            </p>
          </div>
          <div
            className={`px-4 py-3 rounded-xl border ${
              dark
                ? "bg-gray-900/50 border-gray-800"
                : "bg-white border-gray-200"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Check
                className={`w-4 h-4 ${
                  dark ? "text-emerald-400" : "text-emerald-600"
                }`}
              />
              <span
                className={`text-xs font-medium ${
                  dark ? "text-gray-400" : "text-gray-500"
                }`}
              >
                On Your List
              </span>
            </div>
            <p
              className={`text-2xl font-bold ${
                dark ? "text-white" : "text-gray-900"
              }`}
            >
              {concessionsCount}
            </p>
            <p
              className={`text-xs mt-0.5 ${
                dark ? "text-gray-500" : "text-gray-400"
              }`}
            >
              Products in your concessions rules
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search
            className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
              dark ? "text-gray-500" : "text-gray-400"
            }`}
          />
          <input
            type="text"
            placeholder="Search drugs..."
            value={monitorSearch}
            onChange={(e) => setMonitorSearch(e.target.value)}
            className={`w-full pl-9 pr-4 py-2.5 text-sm rounded-xl border outline-none transition-colors ${
              dark
                ? "bg-gray-900 border-gray-800 text-white placeholder:text-gray-500 focus:border-gray-600"
                : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-gray-400"
            }`}
          />
        </div>

        {monitorTab === "current" && (
          <ConcessionCurrentTable
            dark={dark}
            search={monitorSearch}
            isOnList={isOnConcessionsList}
            addToList={addToConcessionsList}
            onAddConcession={onAddConcession}
            onItemClick={setDetailDrug}
            items={monthlyConcessions}
            isLoading={isMonthlyConcessionLoading}
          />
        )}
        {monitorTab === "historical" && (
          <ConcessionHistoricalTable
            dark={dark}
            search={monitorSearch}
            isOnList={isOnConcessionsList}
            addToList={addToConcessionsList}
            onAddConcession={onAddConcession}
            onItemClick={setDetailDrug}
            items={historicalConcessions}
            isLoading={isHistoricalConcessionLoading}
          />
        )}
        {monitorTab === "risk" && (
          <ConcessionRiskTable
            dark={dark}
            search={monitorSearch}
            isOnList={isOnConcessionsList}
            addToList={addToConcessionsList}
            onItemClick={setDetailDrug}
          />
        )}

        <div className="mt-4 px-1">
          <span
            className={`text-[10px] ${
              dark ? "text-gray-600" : "text-gray-400"
            }`}
          >
            Data sourced from CPE NCSO price concessions
          </span>
        </div>
      </div>
    </>
  );
}

// Memoised: these render the full concession list with no pagination, so a
// keystroke in the search box or an unrelated state change re-rendered every row.
const ConcessionCurrentTable = React.memo(function ConcessionCurrentTable({
  dark,
  search,
  isOnList,
  addToList,
  onAddConcession,
  onItemClick,
  items,
  isLoading,
}: {
  dark: boolean;
  search: string;
  isOnList: (d: string) => boolean;
  addToList: (d: string, p: string, dt: string) => void;
  onAddConcession: (item: any) => Promise<any>;
  onItemClick: (d: { name: string; packSize: string; dtPrice: string }) => void;
  items: any[];
  isLoading: boolean;
}) {
  const filtered = search
    ? items.filter((c) =>
        c.productName?.toLowerCase().includes(search.toLowerCase()),
      )
    : items;
  return (
    <div
      className={`rounded-xl border overflow-hidden ${
        dark ? "border-gray-800" : "border-gray-200"
      }`}
    >
      <table className="w-full text-sm">
        <thead>
          <tr className={dark ? "bg-gray-800/60" : "bg-gray-50"}>
            {["Drug", "Pack Size", "DT Price", "Concession Price", ""].map(
              (h) => (
                <th
                  key={h}
                  className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                    dark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {h}
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={5} className="text-center py-12">
                <div
                  className={`w-5 h-5 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-2 ${
                    dark ? "border-gray-500" : "border-gray-400"
                  }`}
                />
                <p
                  className={`text-sm ${
                    dark ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  Loading concessions...
                </p>
              </td>
            </tr>
          ) : filtered.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-center py-12">
                <p
                  className={`text-sm ${
                    dark ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  No matching concessions
                </p>
              </td>
            </tr>
          ) : (
            filtered.map((item, i) => {
              const onList = isOnList(item.productName);
              return (
                <tr
                  key={item.id || i}
                  onClick={() =>
                    onItemClick({
                      name: item.productName,
                      packSize: item.packSize || "—",
                      dtPrice: item.dtPrice
                        ? `£${(item.dtPrice / 100).toFixed(2)}`
                        : "—",
                    })
                  }
                  className={`border-t cursor-pointer ${
                    dark
                      ? "border-gray-800 hover:bg-gray-800/50"
                      : "border-gray-100 hover:bg-gray-50"
                  } ${
                    i % 2 === 0 ? "" : dark ? "bg-gray-900/30" : "bg-gray-50/30"
                  }`}
                >
                  <td
                    className={`px-4 py-3 font-medium ${
                      dark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {item.productName}
                  </td>
                  <td
                    className={`px-4 py-3 ${
                      dark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {item.packSize || "—"}
                  </td>
                  <td
                    className={`px-4 py-3 font-mono text-xs ${
                      dark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {item.dtPrice ? `£${(item.dtPrice / 100).toFixed(2)}` : "—"}
                  </td>
                  <td
                    className={`px-4 py-3 font-mono text-xs font-semibold ${
                      dark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {item.consessionPrice || "—"}
                  </td>
                  <td
                    className="px-4 py-3 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {!onList ? (
                      <button
                        onClick={() => {
                          addToList(
                            item.productName,
                            item.packSize || "",
                            item.dtPrice
                              ? `£${(item.dtPrice / 100).toFixed(2)}`
                              : "—",
                          );
                          onAddConcession(item);
                        }}
                        className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors cursor-pointer ${
                          dark
                            ? "bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 border border-purple-500/30"
                            : "bg-purple-100 text-purple-700 hover:bg-purple-200 border border-purple-200"
                        }`}
                      >
                        <Plus className="w-3 h-3" /> Add to List
                      </button>
                    ) : (
                      <span
                        className={`inline-flex items-center gap-1 text-xs ${
                          dark ? "text-emerald-400" : "text-emerald-600"
                        }`}
                      >
                        <Check className="w-3 h-3" /> Added
                      </span>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
});

// Memoised: these render the full concession list with no pagination, so a
// keystroke in the search box or an unrelated state change re-rendered every row.
const ConcessionHistoricalTable = React.memo(function ConcessionHistoricalTable({
  dark,
  search,
  isOnList,
  addToList,
  onAddConcession,
  onItemClick,
  items,
  isLoading,
}: {
  dark: boolean;
  search: string;
  isOnList: (d: string) => boolean;
  addToList: (d: string, p: string, dt: string) => void;
  onAddConcession: (item: any) => Promise<any>;
  onItemClick: (d: { name: string; packSize: string; dtPrice: string }) => void;
  items: any[];
  isLoading: boolean;
}) {
  const filtered = search
    ? items.filter((c) =>
        c.productName?.toLowerCase().includes(search.toLowerCase()),
      )
    : items;
  const formatMonth = (val: string) => {
    if (!val) return "—";
    const d = new Date(val);
    return isNaN(d.getTime())
      ? val
      : d.toLocaleString("default", { month: "short", year: "numeric" });
  };
  return (
    <div
      className={`rounded-xl border overflow-hidden ${
        dark ? "border-gray-800" : "border-gray-200"
      }`}
    >
      <table className="w-full text-sm">
        <thead>
          <tr className={dark ? "bg-gray-800/60" : "bg-gray-50"}>
            {[
              "Drug",
              "Pack Size",
              "Month",
              "DT Price",
              "Concession Price",
              "",
            ].map((h) => (
              <th
                key={h}
                className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                  dark ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={6} className="text-center py-12">
                <div
                  className={`w-5 h-5 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-2 ${
                    dark ? "border-gray-500" : "border-gray-400"
                  }`}
                />
                <p
                  className={`text-sm ${
                    dark ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  Loading historical concessions...
                </p>
              </td>
            </tr>
          ) : filtered.length === 0 ? (
            <tr>
              <td colSpan={6} className="text-center py-12">
                <p
                  className={`text-sm ${
                    dark ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  No matching historical concessions
                </p>
              </td>
            </tr>
          ) : (
            filtered.map((item, i) => {
              const onList = isOnList(item.productName);
              return (
                <tr
                  key={item.id || i}
                  onClick={() =>
                    onItemClick({
                      name: item.productName,
                      packSize: item.packSize || "—",
                      dtPrice: item.dtPrice
                        ? `£${(item.dtPrice / 100).toFixed(2)}`
                        : "—",
                    })
                  }
                  className={`border-t cursor-pointer ${
                    dark
                      ? "border-gray-800 hover:bg-gray-800/50"
                      : "border-gray-100 hover:bg-gray-50"
                  } ${
                    i % 2 === 0 ? "" : dark ? "bg-gray-900/30" : "bg-gray-50/30"
                  }`}
                >
                  <td
                    className={`px-4 py-3 font-medium ${
                      dark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {item.productName}
                  </td>
                  <td
                    className={`px-4 py-3 ${
                      dark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {item.packSize || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        dark
                          ? "bg-gray-800 text-gray-300"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {formatMonth(item.concessionMonth)}
                    </span>
                  </td>
                  <td
                    className={`px-4 py-3 font-mono text-xs ${
                      dark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {item.dtPrice ? `£${(item.dtPrice / 100).toFixed(2)}` : "—"}
                  </td>
                  <td
                    className={`px-4 py-3 font-mono text-xs font-semibold ${
                      dark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {item.consessionPrice || "—"}
                  </td>
                  <td
                    className="px-4 py-3 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {!onList ? (
                      <button
                        onClick={() => {
                          addToList(
                            item.productName,
                            item.packSize || "",
                            item.dtPrice
                              ? `£${(item.dtPrice / 100).toFixed(2)}`
                              : "—",
                          );
                          onAddConcession(item);
                        }}
                        className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors cursor-pointer ${
                          dark
                            ? "bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 border border-purple-500/30"
                            : "bg-purple-100 text-purple-700 hover:bg-purple-200 border border-purple-200"
                        }`}
                      >
                        <Plus className="w-3 h-3" /> Add to List
                      </button>
                    ) : (
                      <span
                        className={`inline-flex items-center gap-1 text-xs ${
                          dark ? "text-emerald-400" : "text-emerald-600"
                        }`}
                      >
                        <Check className="w-3 h-3" /> Added
                      </span>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
});

// Memoised: these render the full concession list with no pagination, so a
// keystroke in the search box or an unrelated state change re-rendered every row.
const ConcessionRiskTable = React.memo(function ConcessionRiskTable({
  dark,
  search,
  isOnList,
  addToList,
  onItemClick,
}: {
  dark: boolean;
  search: string;
  isOnList: (d: string) => boolean;
  addToList: (d: string, p: string, dt: string) => void;
  onItemClick: (d: { name: string; packSize: string; dtPrice: string }) => void;
}) {
  const filtered = search
    ? CONCESSION_RISK_ITEMS.filter((c) =>
        c.drug.toLowerCase().includes(search.toLowerCase()),
      )
    : CONCESSION_RISK_ITEMS;
  return (
    <>
      <div
        className={`mb-4 px-4 py-3 rounded-xl border ${
          dark
            ? "bg-amber-500/5 border-amber-500/20"
            : "bg-amber-50 border-amber-200"
        }`}
      >
        <p className={`text-xs ${dark ? "text-amber-300" : "text-amber-700"}`}>
          <strong>Risk analysis</strong> is based on how frequently a drug has
          appeared on NCSO price concessions over the past 12 months. Drugs with
          5+ appearances are rated high risk, 3–4 are medium, and 1–2 are low.
        </p>
      </div>
      <div
        className={`rounded-xl border overflow-hidden ${
          dark ? "border-gray-800" : "border-gray-200"
        }`}
      >
        <table className="w-full text-sm">
          <thead>
            <tr className={dark ? "bg-gray-800/60" : "bg-gray-50"}>
              {[
                "Drug",
                "Pack Size",
                "DT Price",
                "Times on Concession",
                "Last Appeared",
                "Risk",
                "Status",
                "",
              ].map((h) => (
                <th
                  key={h}
                  className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                    dark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-12">
                  <p
                    className={`text-sm ${
                      dark ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    No matching drugs
                  </p>
                </td>
              </tr>
            ) : (
              filtered.map((item, i) => {
                const rs = RISK_STYLES[item.risk];
                const onList = isOnList(item.drug);
                return (
                  <tr
                    key={item.id}
                    onClick={() =>
                      onItemClick({
                        name: item.drug,
                        packSize: item.packSize,
                        dtPrice: item.dtPrice,
                      })
                    }
                    className={`border-t cursor-pointer ${
                      dark
                        ? "border-gray-800 hover:bg-gray-800/50"
                        : "border-gray-100 hover:bg-gray-50"
                    } ${
                      i % 2 === 0
                        ? ""
                        : dark
                        ? "bg-gray-900/30"
                        : "bg-gray-50/30"
                    }`}
                  >
                    <td
                      className={`px-4 py-3 font-medium ${
                        dark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {item.drug}
                    </td>
                    <td
                      className={`px-4 py-3 ${
                        dark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {item.packSize}
                    </td>
                    <td
                      className={`px-4 py-3 font-mono text-xs ${
                        dark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {item.dtPrice}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div
                          className={`h-1.5 rounded-full ${
                            dark ? "bg-gray-800" : "bg-gray-200"
                          }`}
                          style={{ width: 60 }}
                        >
                          <div
                            className={`h-1.5 rounded-full ${
                              item.risk === "high"
                                ? "bg-red-500"
                                : item.risk === "medium"
                                ? "bg-amber-500"
                                : "bg-emerald-500"
                            }`}
                            style={{
                              width: `${Math.min(
                                100,
                                (item.timesOnConcession / 8) * 100,
                              )}%`,
                            }}
                          />
                        </div>
                        <span
                          className={`text-xs font-medium ${
                            dark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          {item.timesOnConcession}x
                        </span>
                      </div>
                    </td>
                    <td
                      className={`px-4 py-3 text-xs ${
                        dark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {item.lastOnConcession}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          dark
                            ? `${rs.bg} ${rs.text}`
                            : `${rs.bgLight} ${rs.textLight}`
                        }`}
                      >
                        {rs.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {item.currentlyOn && (
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            dark
                              ? "bg-blue-500/15 text-blue-400"
                              : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          Active Now
                        </span>
                      )}
                    </td>
                    <td
                      className="px-4 py-3 text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {!onList ? (
                        <button
                          onClick={() =>
                            addToList(item.drug, item.packSize, item.dtPrice)
                          }
                          className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors cursor-pointer ${
                            dark
                              ? "bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 border border-purple-500/30"
                              : "bg-purple-100 text-purple-700 hover:bg-purple-200 border border-purple-200"
                          }`}
                        >
                          <Plus className="w-3 h-3" /> Add to List
                        </button>
                      ) : (
                        <span
                          className={`inline-flex items-center gap-1 text-xs ${
                            dark ? "text-emerald-400" : "text-emerald-600"
                          }`}
                        >
                          <Check className="w-3 h-3" /> Added
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
});

// ── CONCESSION DETAIL MODAL ───────────────────────────────────────────────────

function ConcessionDetailModal({
  dark,
  drugName,
  isOnList,
  addToList,
  onClose,
}: {
  dark: boolean;
  drugName: string;
  isOnList: boolean;
  addToList: () => void;
  onClose: () => void;
}) {
  const drugLower = drugName.toLowerCase();

  // Gather data for this drug across all datasets
  const currentEntry = CURRENT_CONCESSIONS.find(
    (c) => c.drug.toLowerCase() === drugLower,
  );
  const historyEntries = HISTORICAL_CONCESSIONS.filter(
    (c) => c.drug.toLowerCase() === drugLower,
  );
  const riskEntry = CONCESSION_RISK_ITEMS.find(
    (c) => c.drug.toLowerCase() === drugLower,
  );

  const rs = riskEntry ? RISK_STYLES[riskEntry.risk] : null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl border shadow-2xl ${
          dark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b ${
            dark ? "bg-gray-900 border-gray-800" : "bg-white border-gray-100"
          }`}
        >
          <div>
            <h3
              className={`text-base font-semibold ${
                dark ? "text-white" : "text-gray-900"
              }`}
            >
              {drugName}
            </h3>
            <p
              className={`text-xs mt-0.5 ${
                dark ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Concession detail &amp; history
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors cursor-pointer ${
              dark
                ? "hover:bg-gray-800 text-gray-400"
                : "hover:bg-gray-100 text-gray-500"
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Current status section */}
          <div>
            <h4
              className={`text-xs font-semibold uppercase tracking-wider mb-3 ${
                dark ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Current Status
            </h4>
            {currentEntry ? (
              <div
                className={`rounded-xl border p-4 ${
                  dark
                    ? "bg-gray-800/50 border-gray-800"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span
                      className={`text-[10px] font-medium uppercase tracking-wider ${
                        dark ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      Pack Size
                    </span>
                    <p
                      className={`text-sm font-medium mt-0.5 ${
                        dark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {currentEntry.packSize}
                    </p>
                  </div>
                  <div>
                    <span
                      className={`text-[10px] font-medium uppercase tracking-wider ${
                        dark ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      Drug Tariff Price
                    </span>
                    <p
                      className={`text-sm font-mono font-medium mt-0.5 ${
                        dark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {currentEntry.dtPrice}
                    </p>
                  </div>
                  <div>
                    <span
                      className={`text-[10px] font-medium uppercase tracking-wider ${
                        dark ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      Concession Price
                    </span>
                    <p
                      className={`text-sm font-mono font-semibold mt-0.5 ${
                        dark ? "text-amber-400" : "text-amber-600"
                      }`}
                    >
                      {currentEntry.concessionPrice}
                    </p>
                  </div>
                  <div>
                    <span
                      className={`text-[10px] font-medium uppercase tracking-wider ${
                        dark ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      Difference
                    </span>
                    <p
                      className={`text-sm font-mono font-semibold mt-0.5 ${
                        dark ? "text-red-400" : "text-red-600"
                      }`}
                    >
                      {currentEntry.difference}
                    </p>
                  </div>
                </div>
                <div
                  className="flex items-center gap-2 mt-3 pt-3 border-t"
                  style={{ borderColor: dark ? "#374151" : "#e5e7eb" }}
                >
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                      dark
                        ? "bg-blue-500/15 text-blue-400"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    Active — March 2026
                  </span>
                  {currentEntry.rolledOver && (
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        dark
                          ? "bg-purple-500/15 text-purple-400"
                          : "bg-purple-100 text-purple-700"
                      }`}
                    >
                      Rolled Over
                    </span>
                  )}
                  {isOnList && (
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        dark
                          ? "bg-emerald-500/15 text-emerald-400"
                          : "bg-emerald-100 text-emerald-700"
                      }`}
                    >
                      On Your List
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div
                className={`rounded-xl border p-4 text-center ${
                  dark
                    ? "bg-gray-800/30 border-gray-800"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <p
                  className={`text-sm ${
                    dark ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  Not currently on concession
                </p>
                {isOnList && (
                  <span
                    className={`inline-flex mt-2 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                      dark
                        ? "bg-emerald-500/15 text-emerald-400"
                        : "bg-emerald-100 text-emerald-700"
                    }`}
                  >
                    On Your List
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Risk section */}
          <div>
            <h4
              className={`text-xs font-semibold uppercase tracking-wider mb-3 ${
                dark ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Risk Rating
            </h4>
            {riskEntry && rs ? (
              <div
                className={`rounded-xl border p-4 ${
                  dark
                    ? "bg-gray-800/50 border-gray-800"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span
                      className={`px-2.5 py-1 rounded-lg text-xs font-bold ${
                        dark
                          ? `${rs.bg} ${rs.text}`
                          : `${rs.bgLight} ${rs.textLight}`
                      }`}
                    >
                      {rs.label} Risk
                    </span>
                    <span
                      className={`text-sm ${
                        dark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      {riskEntry.timesOnConcession} appearances in 12 months
                    </span>
                  </div>
                  {riskEntry.currentlyOn && (
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        dark
                          ? "bg-blue-500/15 text-blue-400"
                          : "bg-blue-100 text-blue-700"
                      }`}
                    >
                      Active Now
                    </span>
                  )}
                </div>
                <div
                  className={`h-2 rounded-full w-full ${
                    dark ? "bg-gray-800" : "bg-gray-200"
                  }`}
                >
                  <div
                    className={`h-2 rounded-full transition-all ${
                      riskEntry.risk === "high"
                        ? "bg-red-500"
                        : riskEntry.risk === "medium"
                        ? "bg-amber-500"
                        : "bg-emerald-500"
                    }`}
                    style={{
                      width: `${Math.min(
                        100,
                        (riskEntry.timesOnConcession / 8) * 100,
                      )}%`,
                    }}
                  />
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span
                    className={`text-[10px] ${
                      dark ? "text-gray-600" : "text-gray-400"
                    }`}
                  >
                    0x
                  </span>
                  <span
                    className={`text-[10px] ${
                      dark ? "text-gray-600" : "text-gray-400"
                    }`}
                  >
                    Last appeared: {riskEntry.lastOnConcession}
                  </span>
                  <span
                    className={`text-[10px] ${
                      dark ? "text-gray-600" : "text-gray-400"
                    }`}
                  >
                    8x+
                  </span>
                </div>
              </div>
            ) : (
              <div
                className={`rounded-xl border p-4 text-center ${
                  dark
                    ? "bg-gray-800/30 border-gray-800"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <p
                  className={`text-sm ${
                    dark ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  No risk data available
                </p>
              </div>
            )}
          </div>

          {/* History section */}
          <div>
            <h4
              className={`text-xs font-semibold uppercase tracking-wider mb-3 ${
                dark ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Concession History
            </h4>
            {historyEntries.length > 0 ? (
              <div
                className={`rounded-xl border overflow-hidden ${
                  dark ? "border-gray-800" : "border-gray-200"
                }`}
              >
                <table className="w-full text-sm">
                  <thead>
                    <tr className={dark ? "bg-gray-800/60" : "bg-gray-50"}>
                      {[
                        "Month",
                        "Pack Size",
                        "Concession Price",
                        "Previous Price",
                      ].map((h) => (
                        <th
                          key={h}
                          className={`px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider ${
                            dark ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {historyEntries.map((h, i) => (
                      <tr
                        key={h.id}
                        className={`border-t ${
                          dark ? "border-gray-800" : "border-gray-100"
                        } ${
                          i % 2 === 0
                            ? ""
                            : dark
                            ? "bg-gray-900/30"
                            : "bg-gray-50/30"
                        }`}
                      >
                        <td className="px-4 py-2.5">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              dark
                                ? "bg-gray-800 text-gray-300"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {h.month}
                          </span>
                        </td>
                        <td
                          className={`px-4 py-2.5 ${
                            dark ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          {h.packSize}
                        </td>
                        <td
                          className={`px-4 py-2.5 font-mono text-xs font-semibold ${
                            dark ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {h.concessionPrice}
                        </td>
                        <td className="px-4 py-2.5">
                          {h.previousPrice ? (
                            <span
                              className={`inline-flex items-center gap-1 font-mono text-xs ${
                                dark ? "text-red-400" : "text-red-600"
                              }`}
                            >
                              <ArrowUpRight className="w-3 h-3" />
                              {h.previousPrice}
                            </span>
                          ) : (
                            <span
                              className={`text-xs ${
                                dark ? "text-gray-600" : "text-gray-400"
                              }`}
                            >
                              —
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div
                className={`rounded-xl border p-4 text-center ${
                  dark
                    ? "bg-gray-800/30 border-gray-800"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <p
                  className={`text-sm ${
                    dark ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  No historical records found
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          className={`sticky bottom-0 flex items-center justify-end gap-3 px-6 py-4 border-t ${
            dark ? "bg-gray-900 border-gray-800" : "bg-white border-gray-100"
          }`}
        >
          {!isOnList ? (
            <button
              onClick={() => {
                addToList();
                onClose();
              }}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-xl transition-colors cursor-pointer ${
                dark
                  ? "bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 border border-purple-500/30"
                  : "bg-purple-100 text-purple-700 hover:bg-purple-200 border border-purple-200"
              }`}
            >
              <Plus className="w-4 h-4" /> Add to Concessions List
            </button>
          ) : (
            <span
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium ${
                dark ? "text-emerald-400" : "text-emerald-600"
              }`}
            >
              <Check className="w-4 h-4" /> Already on your list
            </span>
          )}
          <button
            onClick={onClose}
            className={`px-4 py-2 text-sm font-medium rounded-xl transition-colors cursor-pointer ${
              dark
                ? "bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
            }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function DrugRuleModal({
  dark,
  mode,
  existingProduct,
  categoryLabel,
  defaultActions,
  pharmacies,
  onSave,
  onDelete,
  onClose,
}: {
  dark: boolean;
  mode: "add" | "edit";
  existingProduct?: ProductRuleItem;
  categoryLabel: string;
  defaultActions: ProductRuleAction[];
  pharmacies: Pharmacy[];
  onSave: (data: {
    name: string;
    pack: string;
    manufacturer: string;
    actions: ProductRuleAction[];
    enforcedProduct?: string;
    enforcedPack?: string;
    enforcedProductId?: string;
    expiryDate?: string;
    clinicalWarningText?: string;
    labellingWarningText?: string;
    handoutAlertText?: string;
    existingId?: string;
    dtCategoryId?: string;
    dtCategoryName?: string;
    assignedStores?: string[];
    prescribedItemId?: string;
  }) => void;
  onDelete?: () => void;
  onClose: () => void;
}) {
  const isEdit = mode === "edit" && existingProduct;

  // Step state for add mode: 'search' -> 'refine' -> 'configure'
  const [step, setStep] = useState<"search" | "refine" | "configure">(
    isEdit ? "configure" : "search",
  );
  const [searchMode, setSearchMode] = useState<"name" | "category">("name");
  const [drugSearch, setDrugSearch] = useState("");
  const [selectedGeneric, setSelectedGeneric] = useState<string | null>(null);
  const [selectedDtCategory, setSelectedDtCategory] = useState<string | null>(
    null,
  );
  const [dtCategorySearch, setDtCategorySearch] = useState("");
  const [selectedManufacturer, setSelectedManufacturer] = useState<
    string | null
  >(
    isEdit &&
      existingProduct.manufacturer &&
      existingProduct.manufacturer !== "Any"
      ? existingProduct.manufacturer
      : null,
  );
  const [selectedPack, setSelectedPack] = useState<string | null>(
    isEdit && existingProduct.pack && existingProduct.pack !== "Any"
      ? existingProduct.pack
      : null,
  );

  // API drug search state
  const [apiDrugResults, setApiDrugResults] = useState<any[]>([]);
  const [isApiSearchLoading, setIsApiSearchLoading] = useState(false);
  const [selectedApiDrug, setSelectedApiDrug] = useState<any | null>(null);

  useEffect(() => {
    if (searchMode !== "name" || drugSearch.length < 2) {
      setApiDrugResults([]);
      return;
    }
    setIsApiSearchLoading(true);
    searchProductDrug(drugSearch)
      .then((result: any) => {
        const items = result?.response || (Array.isArray(result) ? result : []);
        setApiDrugResults(items);
        setIsApiSearchLoading(false);
      })
      .catch(() => {
        setApiDrugResults([]);
        setIsApiSearchLoading(false);
      });
  }, [drugSearch, searchMode]);

  // Form state
  const [name, setName] = useState(existingProduct?.name || "");
  const [pack, setPack] = useState(existingProduct?.pack || "");
  const [actions, setActions] = useState<ProductRuleAction[]>(
    existingProduct?.actions || [...defaultActions],
  );
  const [enforcedProduct, setEnforcedProduct] = useState(
    existingProduct?.enforcedProduct || "",
  );
  const [enforcedPack, setEnforcedPack] = useState(
    existingProduct?.enforcedPack || "",
  );
  const [expiryDate, setExpiryDate] = useState(
    existingProduct?.expiryDate || "",
  );
  const [clinicalWarningText, setClinicalWarningText] = useState(
    existingProduct?.clinicalWarningText || "",
  );
  const [labellingWarningText, setLabellingWarningText] = useState(
    existingProduct?.labellingWarningText || "",
  );
  const [handoutAlertText, setHandoutAlertText] = useState(
    existingProduct?.handoutAlertText || "",
  );
  const [assignedStores, setAssignedStores] = useState<string[]>(
    existingProduct?.assignedStores || pharmacies.map((p) => p.pharmacyID),
  );

  // Sync assignedStores when pharmacies load (for new drugs opened before API response)
  useEffect(() => {
    if (
      !existingProduct &&
      pharmacies.length > 0 &&
      assignedStores.length === 0
    ) {
      setAssignedStores(pharmacies.map((p) => p.pharmacyID));
    }
  }, [pharmacies]);

  // Configure tab state
  const [configTab, setConfigTab] = useState<"configure" | "stores" | "audit">(
    "configure",
  );

  // Audit trail state
  const [auditEntries, setAuditEntries] = useState<any[]>([]);
  const [isAuditLoading, setIsAuditLoading] = useState(false);

  const AUDIT_ACTION_LABELS: Record<number, string> = {
    1: "Rule Created",
    2: "Rule Updated",
    3: "Rule Deleted",
    4: "Pharmacy Deactivated",
    5: "Expiry Date Updated",
    6: "Setting Changed",
  };

  useEffect(() => {
    if (!isEdit) return;
    const prescribedItemId =
      existingProduct.prescribedItemId || existingProduct.id;
    const productRuleId = existingProduct.id;
    if (!prescribedItemId || !productRuleId) return;
    setIsAuditLoading(true);
    getProductRuleAudit(prescribedItemId, productRuleId)
      .then((result: any) => {
        const entries = result?.auditEntries || [];
        setAuditEntries(entries);
        setIsAuditLoading(false);
      })
      .catch(() => {
        setAuditEntries([]);
        setIsAuditLoading(false);
      });
  }, []);

  // Force dispensing search state
  const [fdSearch, setFdSearch] = useState("");
  const [fdGeneric, setFdGeneric] = useState<string | null>(null);
  const [fdManufacturers, setFdManufacturers] = useState<string[]>([]);
  const [fdPackChoices, setFdPackChoices] = useState<string[]>([]);
  const [fdStep, setFdStep] = useState<"search" | "refine" | "done">(
    existingProduct?.enforcedProduct ? "done" : "search",
  );
  const [fdSelectedDrug, setFdSelectedDrug] = useState<any | null>(null);
  const [fdApiResults, setFdApiResults] = useState<any[]>([]);
  const [fdApiLoading, setFdApiLoading] = useState(false);

  // API search for Force Dispensing — independent from top-level drug search
  useEffect(() => {
    if (fdSearch.length < 2) {
      setFdApiResults([]);
      return;
    }
    setFdApiLoading(true);
    searchProductDrug(fdSearch)
      .then((result: any) => {
        setFdApiResults(
          result?.response || (Array.isArray(result) ? result : []),
        );
        setFdApiLoading(false);
      })
      .catch(() => {
        setFdApiResults([]);
        setFdApiLoading(false);
      });
  }, [fdSearch]);

  // Group products by generic name for hierarchical search
  const genericGroups = React.useMemo(() => {
    const groups: Record<
      string,
      { manufacturers: string[]; allPacks: string[] }
    > = {};
    DISPENSABLE_PRODUCTS.forEach((dp) => {
      if (!groups[dp.name])
        groups[dp.name] = { manufacturers: [], allPacks: [] };
      if (!groups[dp.name].manufacturers.includes(dp.manufacturer))
        groups[dp.name].manufacturers.push(dp.manufacturer);
      dp.packs.forEach((p) => {
        if (!groups[dp.name].allPacks.includes(p))
          groups[dp.name].allPacks.push(p);
      });
    });
    return groups;
  }, []);

  const genericSearchResults =
    drugSearch.length >= 2
      ? Object.keys(genericGroups)
          .filter((g) => g.toLowerCase().includes(drugSearch.toLowerCase()))
          .slice(0, 10)
      : [];

  // Manufacturers and packs for selected generic (hardcoded fallback)
  const refineManufacturers = selectedGeneric
    ? genericGroups[selectedGeneric]?.manufacturers || []
    : [];
  const refinePacks = React.useMemo(() => {
    if (!selectedGeneric) return [];
    const matching = DISPENSABLE_PRODUCTS.filter(
      (dp) =>
        dp.name === selectedGeneric &&
        (!selectedManufacturer || dp.manufacturer === selectedManufacturer),
    );
    const packs: string[] = [];
    matching.forEach((dp) =>
      dp.packs.forEach((p) => {
        if (!packs.includes(p)) packs.push(p);
      }),
    );
    return packs;
  }, [selectedGeneric, selectedManufacturer]);

  // Refine data driven by API drug selection
  const apiRefineManufacturers: string[] = React.useMemo(() => {
    if (!selectedApiDrug) return [];
    const seen = new Set<string>();
    return selectedApiDrug.manufacturer
      .map((m: any) => m.manufacturerName)
      .filter((name: string) => {
        if (seen.has(name)) return false;
        seen.add(name);
        return true;
      });
  }, [selectedApiDrug]);

  const apiRefinePacks: string[] = React.useMemo(() => {
    if (!selectedApiDrug) return [];
    const mfrs = selectedApiDrug.manufacturer.filter(
      (m: any) =>
        !selectedManufacturer || m.manufacturerName === selectedManufacturer,
    );
    const seen = new Set<string>();
    const result: string[] = [];
    mfrs.forEach((m: any) =>
      m.packSizes.forEach((s: number) => {
        const label = `${Math.round(s)} ${selectedApiDrug.packUnit}`;
        if (!seen.has(label)) {
          seen.add(label);
          result.push(label);
        }
      }),
    );
    return result;
  }, [selectedApiDrug, selectedManufacturer]);

  const selectGeneric = (generic: string) => {
    setSelectedGeneric(generic);
    setSelectedManufacturer(null);
    setSelectedPack(null);
    setStep("refine");
  };

  const selectApiDrug = (drug: any) => {
    setSelectedApiDrug(drug);
    setSelectedGeneric(drug.drugName.trim());
    setSelectedManufacturer(null);
    setSelectedPack(null);
    setStep("refine");
  };

  const [selectedDtCatId, setSelectedDtCatId] = useState<string | null>(
    existingProduct?.dtCategoryId || null,
  );
  const [selectedDtCatName, setSelectedDtCatName] = useState<string | null>(
    existingProduct?.dtCategoryName || null,
  );

  const selectDtItem = (
    itemName: string,
    packs: string[],
    catId: string,
    catName: string,
  ) => {
    setName(itemName);
    setPack(packs[0] || "");
    setSelectedPack(packs.length > 1 ? null : packs[0] || null);
    setSelectedDtCatId(catId);
    setSelectedDtCatName(catName);
    // If multiple packs, go to a mini-refine; otherwise go straight to configure
    if (packs.length > 1) {
      setSelectedGeneric(itemName);
      setStep("refine");
    } else {
      setStep("configure");
    }
  };

  const confirmDrugSelection = () => {
    const baseName = selectedApiDrug
      ? selectedApiDrug.drugName.trim()
      : selectedGeneric || "";
    const displayName = selectedManufacturer
      ? `${baseName} - ${selectedManufacturer}`
      : baseName;
    setName(displayName);
    const availablePacks = selectedApiDrug ? apiRefinePacks : refinePacks;
    setPack(selectedPack || availablePacks[0] || "");
    setStep("configure");
  };

  const toggleModalAction = (action: ProductRuleAction) => {
    setActions((prev) =>
      prev.includes(action)
        ? prev.filter((a) => a !== action)
        : [...prev, action],
    );
    if (action === "forceDispensing" && actions.includes("forceDispensing")) {
      setEnforcedProduct("");
      setEnforcedPack("");
      setFdSearch("");
      setFdGeneric(null);
      setFdManufacturers([]);
      setFdPackChoices([]);
      setFdStep("search");
    }
    if (action === "clinicalWarning" && actions.includes("clinicalWarning")) {
      setClinicalWarningText("");
    }
    if (action === "labellingWarning" && actions.includes("labellingWarning")) {
      setLabellingWarningText("");
    }
    if (action === "handoutAlert" && actions.includes("handoutAlert")) {
      setHandoutAlertText("");
    }
  };

  const toggleStore = (storeId: string) => {
    setAssignedStores((prev) =>
      prev.includes(storeId)
        ? prev.filter((s) => s !== storeId)
        : [...prev, storeId],
    );
  };

  const handleSave = () => {
    if (!name.trim()) return;

    // Build dispenseItems from FD selections against fdSelectedDrug
    // Each entry in manufacturer array has its own productId per packSize
    const dispenseItems: Array<{
      productId: string;
      productName: string;
      manufacturerName: string;
      packSize: number;
    }> = [];
    if (fdSelectedDrug && fdStep === "done") {
      const selectedMfr = fdManufacturers[0] || null; // single-select
      const fdDrugName = fdSelectedDrug.drugName?.trim() || "";
      if (fdPackChoices.length > 0) {
        // For each selected pack, find the exact entry that owns that packSize
        fdPackChoices.forEach((packLabel) => {
          const packNum = parseFloat(packLabel);
          const entry = fdSelectedDrug.manufacturer.find(
            (m: any) =>
              (!selectedMfr || m.manufacturerName === selectedMfr) &&
              m.packSizes.some(
                (s: number) => Math.round(s) === Math.round(packNum),
              ),
          );
          if (entry)
            dispenseItems.push({
              productId: entry.productId,
              productName: fdDrugName,
              manufacturerName: entry.manufacturerName,
              packSize: packNum,
            });
        });
      } else if (selectedMfr) {
        // Manufacturer selected, any pack — add first entry for that manufacturer
        const entry = fdSelectedDrug.manufacturer.find(
          (m: any) => m.manufacturerName === selectedMfr,
        );
        if (entry)
          dispenseItems.push({
            productId: entry.productId,
            productName: fdDrugName,
            manufacturerName: entry.manufacturerName,
            packSize: entry.packSizes[0] || 0,
          });
      } else {
        // Any manufacturer, any pack — use first available entry
        const entry = fdSelectedDrug.manufacturer[0];
        if (entry)
          dispenseItems.push({
            productId: entry.productId,
            productName: fdDrugName,
            manufacturerName: entry.manufacturerName,
            packSize: entry.packSizes[0] || 0,
          });
      }
    }

    onSave({
      name: name.trim(),
      pack: selectedPack || "Any",
      manufacturer: selectedManufacturer || "Any",
      actions,
      enforcedProduct: enforcedProduct || undefined,
      enforcedPack: enforcedPack || undefined,
      dispenseItems: dispenseItems.length > 0 ? dispenseItems : undefined,
      expiryDate: expiryDate || undefined,
      clinicalWarningText: clinicalWarningText.trim() || undefined,
      labellingWarningText: labellingWarningText.trim() || undefined,
      handoutAlertText: handoutAlertText.trim() || undefined,
      existingId: existingProduct?.id,
      dtCategoryId: selectedDtCatId || undefined,
      dtCategoryName: selectedDtCatName || undefined,
      assignedStores: assignedStores,
      prescribedItemId:
        fdSelectedDrug?.prescribedItemId ||
        selectedApiDrug?.prescribedItemId ||
        existingProduct?.id,
    });
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative w-full max-w-lg mx-4 rounded-2xl shadow-2xl border flex flex-col max-h-[85vh] ${
          dark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-6 py-4 border-b shrink-0 ${
            dark ? "border-gray-800" : "border-gray-100"
          }`}
        >
          <div>
            <h3
              className={`text-base font-semibold ${
                dark ? "text-white" : "text-gray-900"
              }`}
            >
              {isEdit ? "Edit Rule" : "Add Drug to"}{" "}
              {!isEdit ? categoryLabel : ""}
            </h3>
            <p
              className={`text-xs mt-0.5 ${
                dark ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {isEdit
                ? `Editing ${existingProduct.name}`
                : step === "search"
                ? searchMode === "name"
                  ? "Search by generic drug name"
                  : "Browse by Drug Tariff category"
                : step === "refine"
                ? "Optionally narrow down by manufacturer & pack"
                : "Configure rule actions"}
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-1.5 rounded-lg cursor-pointer ${
              dark
                ? "hover:bg-gray-800 text-gray-400"
                : "hover:bg-gray-100 text-gray-500"
            }`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Step 1: Search (add mode only) */}
          {step === "search" && (
            <div>
              {/* Search mode toggle */}
              <div
                className={`flex gap-1 p-1 rounded-lg mb-3 ${
                  dark ? "bg-gray-800" : "bg-gray-100"
                }`}
              >
                <button
                  onClick={() => setSearchMode("name")}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer ${
                    searchMode === "name"
                      ? dark
                        ? "bg-gray-700 text-white shadow-sm"
                        : "bg-white text-gray-900 shadow-sm"
                      : dark
                      ? "text-gray-400 hover:text-gray-200"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <Search className="w-3 h-3" /> Search by Name
                </button>
                <button
                  onClick={() => setSearchMode("category")}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer ${
                    searchMode === "category"
                      ? dark
                        ? "bg-gray-700 text-white shadow-sm"
                        : "bg-white text-gray-900 shadow-sm"
                      : dark
                      ? "text-gray-400 hover:text-gray-200"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <Library className="w-3 h-3" /> Drug Tariff Category
                </button>
              </div>

              {searchMode === "name" && (
                <div>
                  <div className="relative mb-3">
                    <Search
                      className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                        dark ? "text-gray-500" : "text-gray-400"
                      }`}
                    />
                    <input
                      type="text"
                      placeholder="Search by generic drug name..."
                      value={drugSearch}
                      onChange={(e) => setDrugSearch(e.target.value)}
                      autoFocus
                      className={`w-full pl-9 pr-4 py-2.5 text-sm rounded-xl border outline-none transition-colors ${
                        dark
                          ? "bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-purple-500"
                          : "bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-purple-400"
                      }`}
                    />
                  </div>
                  <div
                    className={`rounded-xl border overflow-hidden ${
                      dark ? "border-gray-800" : "border-gray-200"
                    }`}
                  >
                    {drugSearch.length < 2 ? (
                      <div
                        className={`px-4 py-8 text-center ${
                          dark ? "text-gray-500" : "text-gray-400"
                        }`}
                      >
                        <Search className="w-6 h-6 mx-auto mb-2 opacity-40" />
                        <p className="text-xs">
                          Type at least 2 characters to search
                        </p>
                      </div>
                    ) : isApiSearchLoading ? (
                      <div
                        className={`px-4 py-8 text-center ${
                          dark ? "text-gray-500" : "text-gray-400"
                        }`}
                      >
                        <p className="text-xs">Searching...</p>
                      </div>
                    ) : apiDrugResults.length === 0 ? (
                      <div
                        className={`px-4 py-8 text-center ${
                          dark ? "text-gray-500" : "text-gray-400"
                        }`}
                      >
                        <p className="text-xs">No matching drugs found</p>
                      </div>
                    ) : (
                      <div className="max-h-[300px] overflow-y-auto">
                        {apiDrugResults.map((drug, idx) => (
                          <button
                            key={drug.prescribedItemId}
                            onClick={() => selectApiDrug(drug)}
                            className={`w-full text-left px-4 py-3 text-sm cursor-pointer transition-colors flex items-center justify-between ${
                              dark
                                ? "hover:bg-gray-800 text-gray-200"
                                : "hover:bg-gray-50 text-gray-800"
                            } ${
                              idx > 0
                                ? `border-t ${
                                    dark ? "border-gray-800" : "border-gray-100"
                                  }`
                                : ""
                            }`}
                          >
                            <div>
                              <span className="font-medium">
                                {drug.drugName.trim()}
                              </span>
                              <div
                                className={`text-[11px] mt-0.5 flex items-center gap-2 ${
                                  dark ? "text-gray-500" : "text-gray-400"
                                }`}
                              >
                                <span>
                                  {drug.totalManufacturers} manufacturer
                                  {drug.totalManufacturers !== 1 ? "s" : ""}
                                </span>
                                <span>·</span>
                                <span>
                                  {drug.totalPackSizes} pack size
                                  {drug.totalPackSizes !== 1 ? "s" : ""}
                                </span>
                                <span>·</span>
                                <span>{drug.packUnit}</span>
                              </div>
                            </div>
                            <ChevronRight
                              className={`w-4 h-4 shrink-0 ${
                                dark ? "text-gray-600" : "text-gray-300"
                              }`}
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {searchMode === "category" && !selectedDtCategory && (
                <div>
                  <div
                    className={`rounded-xl border overflow-hidden ${
                      dark ? "border-gray-800" : "border-gray-200"
                    }`}
                  >
                    <div className="max-h-[340px] overflow-y-auto">
                      {DRUG_TARIFF_CATEGORIES.map((cat, idx) => (
                        <button
                          key={cat.id}
                          onClick={() => {
                            setSelectedDtCategory(cat.id);
                            setDtCategorySearch("");
                          }}
                          className={`w-full text-left px-4 py-3 text-sm cursor-pointer transition-colors flex items-center justify-between ${
                            dark
                              ? "hover:bg-gray-800 text-gray-200"
                              : "hover:bg-gray-50 text-gray-800"
                          } ${
                            idx > 0
                              ? `border-t ${
                                  dark ? "border-gray-800" : "border-gray-100"
                                }`
                              : ""
                          }`}
                        >
                          <div>
                            <span className="font-medium">{cat.name}</span>
                            <p
                              className={`text-[11px] mt-0.5 ${
                                dark ? "text-gray-500" : "text-gray-400"
                              }`}
                            >
                              {cat.items.length} item
                              {cat.items.length !== 1 ? "s" : ""}
                            </p>
                          </div>
                          <ChevronRight
                            className={`w-4 h-4 shrink-0 ${
                              dark ? "text-gray-600" : "text-gray-300"
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {searchMode === "category" &&
                selectedDtCategory &&
                (() => {
                  const cat = DRUG_TARIFF_CATEGORIES.find(
                    (c) => c.id === selectedDtCategory,
                  );
                  if (!cat) return null;
                  const filteredItems =
                    dtCategorySearch.length >= 2
                      ? cat.items.filter((i) =>
                          i.name
                            .toLowerCase()
                            .includes(dtCategorySearch.toLowerCase()),
                        )
                      : cat.items;
                  return (
                    <div>
                      <div
                        className={`flex items-center gap-2 px-3 py-2 rounded-xl border mb-3 ${
                          dark
                            ? "bg-gray-800 border-gray-700"
                            : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <Library
                          className={`w-4 h-4 shrink-0 ${
                            dark ? "text-purple-400" : "text-purple-600"
                          }`}
                        />
                        <span
                          className={`text-sm font-medium flex-1 ${
                            dark ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {cat.name}
                        </span>
                        <button
                          onClick={() => setSelectedDtCategory(null)}
                          className={`text-xs px-2 py-1 rounded-lg cursor-pointer ${
                            dark
                              ? "text-purple-400 hover:bg-purple-500/10"
                              : "text-purple-600 hover:bg-purple-50"
                          }`}
                        >
                          Change
                        </button>
                      </div>
                      <div className="relative mb-3">
                        <Search
                          className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                            dark ? "text-gray-500" : "text-gray-400"
                          }`}
                        />
                        <input
                          type="text"
                          placeholder={`Filter items in ${cat.name.split(
                            " - ",
                          )[1] || cat.name}...`}
                          value={dtCategorySearch}
                          onChange={(e) => setDtCategorySearch(e.target.value)}
                          className={`w-full pl-9 pr-4 py-2 text-sm rounded-xl border outline-none transition-colors ${
                            dark
                              ? "bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-purple-500"
                              : "bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-purple-400"
                          }`}
                        />
                      </div>
                      <div
                        className={`rounded-xl border overflow-hidden ${
                          dark ? "border-gray-800" : "border-gray-200"
                        }`}
                      >
                        {filteredItems.length === 0 ? (
                          <div
                            className={`px-4 py-6 text-center ${
                              dark ? "text-gray-500" : "text-gray-400"
                            }`}
                          >
                            <p className="text-xs">No matching items</p>
                          </div>
                        ) : (
                          <div className="max-h-[260px] overflow-y-auto">
                            {filteredItems.map((item, idx) => (
                              <button
                                key={idx}
                                onClick={() =>
                                  selectDtItem(
                                    item.name,
                                    item.packs,
                                    cat.id,
                                    cat.name,
                                  )
                                }
                                className={`w-full text-left px-4 py-2.5 text-sm cursor-pointer transition-colors flex items-center justify-between ${
                                  dark
                                    ? "hover:bg-gray-800 text-gray-200"
                                    : "hover:bg-gray-50 text-gray-800"
                                } ${
                                  idx > 0
                                    ? `border-t ${
                                        dark
                                          ? "border-gray-800"
                                          : "border-gray-100"
                                      }`
                                    : ""
                                }`}
                              >
                                <div>
                                  <span className="font-medium">
                                    {item.name}
                                  </span>
                                  <p
                                    className={`text-[11px] mt-0.5 ${
                                      dark ? "text-gray-500" : "text-gray-400"
                                    }`}
                                  >
                                    {item.packs.join(", ")}
                                  </p>
                                </div>
                                <ChevronRight
                                  className={`w-4 h-4 shrink-0 ${
                                    dark ? "text-gray-600" : "text-gray-300"
                                  }`}
                                />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
            </div>
          )}

          {/* Step 2: Refine by manufacturer & pack (optional) */}
          {step === "refine" && selectedGeneric && (
            <div className="space-y-4">
              {/* Selected generic display */}
              <div
                className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border ${
                  dark
                    ? "bg-gray-800 border-gray-700"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <Pill
                  className={`w-4 h-4 shrink-0 ${
                    dark ? "text-purple-400" : "text-purple-600"
                  }`}
                />
                <span
                  className={`text-sm font-medium flex-1 ${
                    dark ? "text-white" : "text-gray-900"
                  }`}
                >
                  {selectedGeneric}
                </span>
                <button
                  onClick={() => {
                    setStep("search");
                    setDrugSearch("");
                  }}
                  className={`text-xs px-2 py-1 rounded-lg cursor-pointer ${
                    dark
                      ? "text-purple-400 hover:bg-purple-500/10"
                      : "text-purple-600 hover:bg-purple-50"
                  }`}
                >
                  Change
                </button>
              </div>

              {/* Manufacturer filter (optional) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label
                    className={`text-xs font-medium ${
                      dark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Manufacturer
                  </label>
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded ${
                      dark
                        ? "bg-gray-800 text-gray-500"
                        : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    Optional
                  </span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => setSelectedManufacturer(null)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors cursor-pointer ${
                      !selectedManufacturer
                        ? dark
                          ? "bg-purple-500/20 text-purple-300 border-purple-500/40"
                          : "bg-purple-100 text-purple-700 border-purple-200"
                        : dark
                        ? "bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-600"
                        : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    Any Manufacturer
                  </button>
                  {(selectedApiDrug
                    ? apiRefineManufacturers
                    : refineManufacturers
                  ).map((m) => (
                    <button
                      key={m}
                      onClick={() => setSelectedManufacturer(m)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors cursor-pointer ${
                        selectedManufacturer === m
                          ? dark
                            ? "bg-purple-500/20 text-purple-300 border-purple-500/40"
                            : "bg-purple-100 text-purple-700 border-purple-200"
                          : dark
                          ? "bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-600"
                          : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              {/* Pack size filter (optional) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label
                    className={`text-xs font-medium ${
                      dark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Pack Size
                  </label>
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded ${
                      dark
                        ? "bg-gray-800 text-gray-500"
                        : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    Optional
                  </span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => setSelectedPack(null)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors cursor-pointer ${
                      !selectedPack
                        ? dark
                          ? "bg-purple-500/20 text-purple-300 border-purple-500/40"
                          : "bg-purple-100 text-purple-700 border-purple-200"
                        : dark
                        ? "bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-600"
                        : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    Any Pack
                  </button>
                  {(selectedApiDrug ? apiRefinePacks : refinePacks).map((p) => (
                    <button
                      key={p}
                      onClick={() => setSelectedPack(p)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors cursor-pointer ${
                        selectedPack === p
                          ? dark
                            ? "bg-purple-500/20 text-purple-300 border-purple-500/40"
                            : "bg-purple-100 text-purple-700 border-purple-200"
                          : dark
                          ? "bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-600"
                          : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Summary + Continue */}
              <div
                className={`rounded-xl border p-3 ${
                  dark
                    ? "bg-gray-800/50 border-gray-700"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <p
                  className={`text-[11px] font-medium mb-1 ${
                    dark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Rule will apply to:
                </p>
                <p
                  className={`text-sm font-medium ${
                    dark ? "text-white" : "text-gray-900"
                  }`}
                >
                  {selectedGeneric}
                  {selectedManufacturer
                    ? ` — ${selectedManufacturer}`
                    : " (any manufacturer)"}
                  {selectedPack ? ` · ${selectedPack}` : " · any pack"}
                </p>
              </div>

              <button
                onClick={confirmDrugSelection}
                className={`w-full py-2.5 text-sm font-medium rounded-xl cursor-pointer transition-colors ${
                  dark
                    ? "bg-purple-600 text-white hover:bg-purple-500"
                    : "bg-gray-900 text-white hover:bg-gray-800"
                }`}
              >
                Continue to Configure Rules
              </button>
            </div>
          )}

          {/* Step 2: Configure (both add + edit) */}
          {step === "configure" && (
            <div className="space-y-5">
              {/* Configure / Stores / Audit tabs */}
              <div
                className={`flex items-center gap-4 border-b ${
                  dark ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <button
                  onClick={() => setConfigTab("configure")}
                  className={`flex items-center gap-1.5 pb-2 text-xs font-medium border-b-2 -mb-px transition-colors cursor-pointer ${
                    configTab === "configure"
                      ? dark
                        ? "border-gray-300 text-white"
                        : "border-gray-900 text-gray-900"
                      : `border-transparent ${
                          dark
                            ? "text-gray-400 hover:text-gray-200"
                            : "text-gray-500 hover:text-gray-700"
                        }`
                  }`}
                >
                  Configure
                </button>
                <button
                  onClick={() => setConfigTab("stores")}
                  className={`flex items-center gap-1.5 pb-2 text-xs font-medium border-b-2 -mb-px transition-colors cursor-pointer ${
                    configTab === "stores"
                      ? dark
                        ? "border-gray-300 text-white"
                        : "border-gray-900 text-gray-900"
                      : `border-transparent ${
                          dark
                            ? "text-gray-400 hover:text-gray-200"
                            : "text-gray-500 hover:text-gray-700"
                        }`
                  }`}
                >
                  Stores
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                      assignedStores.length === pharmacies.length
                        ? dark
                          ? "bg-emerald-500/15 text-emerald-400"
                          : "bg-emerald-100 text-emerald-700"
                        : dark
                        ? "bg-purple-500/15 text-purple-400"
                        : "bg-purple-100 text-purple-700"
                    }`}
                  >
                    {assignedStores.length === pharmacies.length
                      ? "All"
                      : `${assignedStores.length}/${pharmacies.length}`}
                  </span>
                </button>
                {isEdit && (
                  <button
                    onClick={() => setConfigTab("audit")}
                    className={`flex items-center gap-1.5 pb-2 text-xs font-medium border-b-2 -mb-px transition-colors cursor-pointer ${
                      configTab === "audit"
                        ? dark
                          ? "border-gray-300 text-white"
                          : "border-gray-900 text-gray-900"
                        : `border-transparent ${
                            dark
                              ? "text-gray-400 hover:text-gray-200"
                              : "text-gray-500 hover:text-gray-700"
                          }`
                    }`}
                  >
                    Audit Trail
                  </button>
                )}
              </div>

              {/* Audit tab content */}
              {configTab === "audit" && isEdit && (
                <div className="space-y-1">
                  {isAuditLoading ? (
                    <div className="space-y-3 py-2">
                      {[...Array(4)].map((_, i) => (
                        <div key={i} className="flex gap-3 py-2 animate-pulse">
                          <div
                            className={`w-2 h-2 rounded-full mt-1 shrink-0 ml-[14px] ${
                              dark ? "bg-gray-700" : "bg-gray-200"
                            }`}
                          />
                          <div className="flex-1 space-y-1.5">
                            <div
                              className={`h-3 rounded w-2/5 ${
                                dark ? "bg-gray-700" : "bg-gray-200"
                              }`}
                            />
                            <div
                              className={`h-2.5 rounded w-4/5 ${
                                dark ? "bg-gray-800" : "bg-gray-100"
                              }`}
                            />
                            <div
                              className={`h-2 rounded w-1/3 ${
                                dark ? "bg-gray-800" : "bg-gray-100"
                              }`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      <div
                        className={`text-xs font-medium mb-3 ${
                          dark ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {auditEntries.length} audit entries
                      </div>
                      <div className="relative">
                        <div
                          className={`absolute left-[15px] top-2 bottom-2 w-px ${
                            dark ? "bg-gray-800" : "bg-gray-200"
                          }`}
                        />
                        <div className="space-y-0">
                          {auditEntries
                            .slice()
                            .reverse()
                            .map((entry, i) => {
                              const d = new Date(entry.createdDate);
                              const dateStr = d.toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              });
                              const timeStr = d.toLocaleTimeString("en-GB", {
                                hour: "2-digit",
                                minute: "2-digit",
                              });
                              const actionLabel =
                                AUDIT_ACTION_LABELS[entry.action] ??
                                `Action ${entry.action}`;
                              return (
                                <div
                                  key={i}
                                  className={`relative flex gap-3 py-3 ${
                                    i > 0
                                      ? `border-t ${
                                          dark
                                            ? "border-gray-800/50"
                                            : "border-gray-100/50"
                                        }`
                                      : ""
                                  }`}
                                >
                                  <div className="relative z-10 w-[31px] shrink-0 flex items-start justify-center pt-0.5">
                                    <div
                                      className={`w-2 h-2 rounded-full ${
                                        i === 0
                                          ? "bg-purple-500"
                                          : dark
                                          ? "bg-gray-600"
                                          : "bg-gray-300"
                                      }`}
                                    />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2">
                                      <span
                                        className={`text-xs font-semibold ${
                                          dark ? "text-white" : "text-gray-900"
                                        }`}
                                      >
                                        {actionLabel}
                                      </span>
                                      <span
                                        className={`text-[10px] shrink-0 ${
                                          dark
                                            ? "text-gray-600"
                                            : "text-gray-400"
                                        }`}
                                      >
                                        {dateStr} \u00b7 {timeStr}
                                      </span>
                                    </div>
                                    <div
                                      className={`text-[11px] mt-0.5 ${
                                        dark ? "text-gray-400" : "text-gray-500"
                                      }`}
                                    >
                                      {entry.details}
                                    </div>
                                    <div
                                      className={`text-[10px] mt-1 ${
                                        dark ? "text-gray-600" : "text-gray-400"
                                      }`}
                                    >
                                      {entry.userName}
                                      {entry.userRole
                                        ? ` \u00b7 ${entry.userRole}`
                                        : ""}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          {auditEntries.length === 0 && (
                            <div
                              className={`py-6 text-center text-xs ${
                                dark ? "text-gray-500" : "text-gray-400"
                              }`}
                            >
                              No audit entries found.
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Stores tab content */}
              {configTab === "stores" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p
                        className={`text-xs font-medium ${
                          dark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Store Assignment
                      </p>
                      <p
                        className={`text-[10px] mt-0.5 ${
                          dark ? "text-gray-500" : "text-gray-400"
                        }`}
                      >
                        Choose which stores this rule applies to
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        setAssignedStores((prev) =>
                          prev.length === pharmacies.length
                            ? []
                            : pharmacies.map((p) => p.pharmacyID),
                        )
                      }
                      className={`text-[11px] font-medium px-2 py-1 rounded-lg cursor-pointer transition-colors ${
                        dark
                          ? "text-purple-400 hover:bg-purple-500/10"
                          : "text-purple-600 hover:bg-purple-50"
                      }`}
                    >
                      {assignedStores.length === pharmacies.length
                        ? "Deselect All"
                        : "Select All"}
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {pharmacies.map((pharmacy, idx) => {
                      const color = BRANCH_COLORS[idx % BRANCH_COLORS.length];
                      const isSelected = assignedStores.includes(
                        pharmacy.pharmacyID,
                      );
                      return (
                        <button
                          key={pharmacy.pharmacyID}
                          onClick={() => toggleStore(pharmacy.pharmacyID)}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-sm transition-all cursor-pointer ${
                            isSelected
                              ? dark
                                ? "border-transparent"
                                : "border-transparent"
                              : dark
                              ? "bg-gray-800/50 text-gray-500 border-gray-800 hover:border-gray-700"
                              : "bg-gray-50 text-gray-400 border-gray-200 hover:border-gray-300"
                          }`}
                          style={
                            isSelected
                              ? {
                                  backgroundColor: `${color}12`,
                                  borderColor: `${color}40`,
                                  color,
                                }
                              : undefined
                          }
                        >
                          <span
                            className="w-2.5 h-2.5 rounded-full shrink-0"
                            style={{
                              background: isSelected
                                ? color
                                : dark
                                ? "#374151"
                                : "#d1d5db",
                            }}
                          />
                          <span
                            className={`flex-1 text-left text-xs font-medium ${
                              isSelected
                                ? ""
                                : dark
                                ? "text-gray-400"
                                : "text-gray-500"
                            }`}
                            style={
                              isSelected
                                ? { color: dark ? "#e5e7eb" : "#1f2937" }
                                : undefined
                            }
                          >
                            {pharmacy.pharmacyName}
                          </span>
                          {isSelected && <Check className="w-3.5 h-3.5" />}
                        </button>
                      );
                    })}
                  </div>
                  {assignedStores.length === 0 && (
                    <div
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border ${
                        dark
                          ? "bg-red-950/20 border-red-500/20"
                          : "bg-red-50 border-red-200"
                      }`}
                    >
                      <AlertTriangle
                        className={`w-3.5 h-3.5 shrink-0 ${
                          dark ? "text-red-400" : "text-red-500"
                        }`}
                      />
                      <p
                        className={`text-[11px] font-medium ${
                          dark ? "text-red-400" : "text-red-600"
                        }`}
                      >
                        Select at least one store for this rule to apply.
                      </p>
                    </div>
                  )}
                  {assignedStores.length > 0 && (
                    <p
                      className={`text-[10px] text-center ${
                        dark ? "text-gray-500" : "text-gray-400"
                      }`}
                    >
                      {assignedStores.length} of {pharmacies.length} stores
                      selected
                    </p>
                  )}
                </div>
              )}

              {/* Configure tab content */}
              {configTab === "configure" && (
                <>
                  {/* Drug name */}
                  <div>
                    <label
                      className={`block text-xs font-medium mb-1.5 ${
                        dark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Drug
                    </label>
                    <div
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border ${
                        dark
                          ? "bg-gray-800 border-gray-700"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <Pill
                        className={`w-4 h-4 shrink-0 ${
                          dark ? "text-gray-500" : "text-gray-400"
                        }`}
                      />
                      <span
                        className={`text-sm font-medium flex-1 ${
                          dark ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {name}
                      </span>
                      {!isEdit && (
                        <button
                          onClick={() => {
                            setStep("search");
                            setDrugSearch("");
                          }}
                          className={`text-xs px-2 py-1 rounded-lg cursor-pointer ${
                            dark
                              ? "text-purple-400 hover:bg-purple-500/10"
                              : "text-purple-600 hover:bg-purple-50"
                          }`}
                        >
                          Change
                        </button>
                      )}
                    </div>
                    {/* Show specificity details */}
                    {(!selectedManufacturer || !selectedPack) && (
                      <div
                        className={`flex items-center gap-1.5 mt-1.5 px-3 ${
                          dark ? "text-gray-500" : "text-gray-400"
                        }`}
                      >
                        {!selectedManufacturer && (
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded ${
                              dark ? "bg-gray-800" : "bg-gray-100"
                            }`}
                          >
                            Any manufacturer
                          </span>
                        )}
                        {!selectedPack && (
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded ${
                              dark ? "bg-gray-800" : "bg-gray-100"
                            }`}
                          >
                            Any pack
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div>
                    <label
                      className={`block text-xs font-medium mb-2 ${
                        dark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Rule Actions
                    </label>
                    <div className="space-y-2">
                      {(Object.keys(
                        PRODUCT_RULE_ACTION_LABELS,
                      ) as ProductRuleAction[]).map((action) => {
                        const isActive = actions.includes(action);
                        const c = PRODUCT_RULE_ACTION_COLORS[action];
                        const isFD = action === "forceDispensing";
                        const isCW = action === "clinicalWarning";
                        const isLW = action === "labellingWarning";
                        const isHA = action === "handoutAlert";
                        const hasExpandable = isFD || isCW || isLW || isHA;
                        const fdMissing = isFD && isActive && !enforcedProduct;
                        return (
                          <div key={action}>
                            <button
                              onClick={() => toggleModalAction(action)}
                              className={`w-full flex items-center gap-3 px-3 py-2.5 ${
                                hasExpandable && isActive
                                  ? "rounded-t-xl"
                                  : "rounded-xl"
                              } border text-sm transition-colors cursor-pointer ${
                                isActive
                                  ? dark
                                    ? `${c.bg} ${c.text} border-transparent`
                                    : `${c.bgLight} ${c.textLight} border-transparent`
                                  : dark
                                  ? "bg-gray-800/50 text-gray-500 border-gray-800 hover:border-gray-700"
                                  : "bg-gray-50 text-gray-400 border-gray-200 hover:border-gray-300"
                              }`}
                            >
                              <div
                                className={`p-1 rounded-lg ${
                                  isActive
                                    ? dark
                                      ? "bg-black/20"
                                      : "bg-white/60"
                                    : ""
                                }`}
                              >
                                {PRODUCT_RULE_ACTION_ICONS[action]}
                              </div>
                              <div className="flex-1 text-left">
                                <span className="font-medium">
                                  {PRODUCT_RULE_ACTION_LABELS[action]}
                                </span>
                                <p
                                  className={`text-[10px] mt-0.5 font-normal leading-snug ${
                                    isActive
                                      ? "opacity-70"
                                      : dark
                                      ? "text-gray-600"
                                      : "text-gray-400"
                                  }`}
                                >
                                  {PRODUCT_RULE_ACTION_DESCRIPTIONS[action]}
                                </p>
                              </div>
                              {isFD && isActive && fdMissing && (
                                <span
                                  className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                    dark
                                      ? "bg-red-500/20 text-red-400"
                                      : "bg-red-100 text-red-600"
                                  }`}
                                >
                                  Required
                                </span>
                              )}
                              {isActive && <Check className="w-4 h-4" />}
                            </button>

                            {/* Clinical Warning: inline text input */}
                            {isCW && isActive && (
                              <div
                                className={`px-3 pb-3 pt-2 rounded-b-xl border border-t-0 ${
                                  dark
                                    ? "bg-orange-950/20 border-transparent"
                                    : "bg-orange-50/50 border-transparent"
                                }`}
                              >
                                <p
                                  className={`text-[11px] mb-2 font-medium ${
                                    dark ? "text-orange-400" : "text-orange-600"
                                  }`}
                                >
                                  Warning message to display at clinical check:
                                </p>
                                <textarea
                                  value={clinicalWarningText}
                                  onChange={(e) =>
                                    setClinicalWarningText(e.target.value)
                                  }
                                  placeholder="Enter the warning text to show the pharmacist..."
                                  rows={3}
                                  className={`w-full px-3 py-2 text-sm rounded-lg border outline-none transition-colors resize-none ${
                                    dark
                                      ? "bg-gray-800 border-gray-700 text-white placeholder:text-gray-600 focus:border-orange-500"
                                      : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-orange-400"
                                  }`}
                                />
                                {clinicalWarningText && (
                                  <p
                                    className={`text-[10px] mt-1.5 ${
                                      dark ? "text-gray-500" : "text-gray-400"
                                    }`}
                                  >
                                    {clinicalWarningText.length} characters
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Labelling Warning: inline text input */}
                            {isLW && isActive && (
                              <div
                                className={`px-3 pb-3 pt-2 rounded-b-xl border border-t-0 ${
                                  dark
                                    ? "bg-rose-950/20 border-transparent"
                                    : "bg-rose-50/50 border-transparent"
                                }`}
                              >
                                <p
                                  className={`text-[11px] mb-2 font-medium ${
                                    dark ? "text-rose-400" : "text-rose-600"
                                  }`}
                                >
                                  Warning message to display at labelling:
                                </p>
                                <textarea
                                  value={labellingWarningText}
                                  onChange={(e) =>
                                    setLabellingWarningText(e.target.value)
                                  }
                                  placeholder="Enter the warning text to show when labelling this drug..."
                                  rows={3}
                                  className={`w-full px-3 py-2 text-sm rounded-lg border outline-none transition-colors resize-none ${
                                    dark
                                      ? "bg-gray-800 border-gray-700 text-white placeholder:text-gray-600 focus:border-rose-500"
                                      : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-rose-400"
                                  }`}
                                />
                                {labellingWarningText && (
                                  <p
                                    className={`text-[10px] mt-1.5 ${
                                      dark ? "text-gray-500" : "text-gray-400"
                                    }`}
                                  >
                                    {labellingWarningText.length} characters
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Handout Alert: inline text input */}
                            {isHA && isActive && (
                              <div
                                className={`px-3 pb-3 pt-2 rounded-b-xl border border-t-0 ${
                                  dark
                                    ? "bg-violet-950/20 border-transparent"
                                    : "bg-violet-50/50 border-transparent"
                                }`}
                              >
                                <p
                                  className={`text-[11px] mb-2 font-medium ${
                                    dark ? "text-violet-400" : "text-violet-600"
                                  }`}
                                >
                                  Alert message to show on TITAN Mobile at
                                  handout:
                                </p>
                                <textarea
                                  value={handoutAlertText}
                                  onChange={(e) =>
                                    setHandoutAlertText(e.target.value)
                                  }
                                  placeholder="Enter the message to display to the patient at handout..."
                                  rows={3}
                                  className={`w-full px-3 py-2 text-sm rounded-lg border outline-none transition-colors resize-none ${
                                    dark
                                      ? "bg-gray-800 border-gray-700 text-white placeholder:text-gray-600 focus:border-violet-500"
                                      : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-violet-400"
                                  }`}
                                />
                                {handoutAlertText && (
                                  <p
                                    className={`text-[10px] mt-1.5 ${
                                      dark ? "text-gray-500" : "text-gray-400"
                                    }`}
                                  >
                                    {handoutAlertText.length} characters
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Force Dispensing: inline product search (mandatory) */}
                            {isFD && isActive && (
                              <div
                                className={`px-3 pb-3 pt-2 rounded-b-xl border border-t-0 ${
                                  dark
                                    ? "bg-blue-950/20 border-transparent"
                                    : "bg-blue-50/50 border-transparent"
                                }`}
                              >
                                <p
                                  className={`text-[11px] mb-2 font-medium ${
                                    fdMissing
                                      ? dark
                                        ? "text-red-400"
                                        : "text-red-600"
                                      : dark
                                      ? "text-blue-400"
                                      : "text-blue-600"
                                  }`}
                                >
                                  {fdMissing
                                    ? "Select the product to enforce dispensing of"
                                    : "Enforcing dispensing of:"}
                                </p>

                                {/* FD Done state — product selected */}
                                {fdStep === "done" && enforcedProduct ? (
                                  <div>
                                    <div
                                      className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                                        dark
                                          ? "bg-gray-800"
                                          : "bg-white border border-gray-200"
                                      }`}
                                    >
                                      <Pill
                                        className={`w-3.5 h-3.5 shrink-0 ${
                                          dark
                                            ? "text-blue-400"
                                            : "text-blue-600"
                                        }`}
                                      />
                                      <span
                                        className={`text-xs font-medium flex-1 ${
                                          dark ? "text-white" : "text-gray-900"
                                        }`}
                                      >
                                        {enforcedProduct}
                                      </span>
                                      {enforcedPack && (
                                        <span
                                          className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                            dark
                                              ? "bg-blue-500/15 text-blue-400"
                                              : "bg-blue-100 text-blue-700"
                                          }`}
                                        >
                                          {enforcedPack}
                                        </span>
                                      )}
                                      {!(
                                        selectedApiDrug && selectedManufacturer
                                      ) && (
                                        <button
                                          onClick={() => {
                                            setFdStep("search");
                                            setFdSearch("");
                                            setFdGeneric(null);
                                            setFdManufacturers([]);
                                            setFdPackChoices([]);
                                            setEnforcedProduct("");
                                            setEnforcedPack("");
                                          }}
                                          className={`p-0.5 rounded cursor-pointer ${
                                            dark
                                              ? "text-gray-500 hover:text-gray-300"
                                              : "text-gray-400 hover:text-gray-600"
                                          }`}
                                        >
                                          <X className="w-3 h-3" />
                                        </button>
                                      )}
                                    </div>
                                    {/* Specificity badges */}
                                    <div
                                      className={`flex items-center gap-1.5 mt-1.5 flex-wrap ${
                                        dark ? "text-gray-500" : "text-gray-400"
                                      }`}
                                    >
                                      {fdManufacturers.length === 0 && (
                                        <span
                                          className={`text-[10px] px-1.5 py-0.5 rounded ${
                                            dark ? "bg-gray-800" : "bg-gray-100"
                                          }`}
                                        >
                                          Any manufacturer
                                        </span>
                                      )}
                                      {fdManufacturers.length > 0 &&
                                        fdManufacturers.map((m) => (
                                          <span
                                            key={m}
                                            className={`text-[10px] px-1.5 py-0.5 rounded ${
                                              dark
                                                ? "bg-blue-500/15 text-blue-400"
                                                : "bg-blue-100 text-blue-700"
                                            }`}
                                          >
                                            {m}
                                          </span>
                                        ))}
                                      {fdPackChoices.length === 0 && (
                                        <span
                                          className={`text-[10px] px-1.5 py-0.5 rounded ${
                                            dark ? "bg-gray-800" : "bg-gray-100"
                                          }`}
                                        >
                                          Any pack
                                        </span>
                                      )}
                                      {fdPackChoices.length > 0 &&
                                        fdPackChoices.map((p) => (
                                          <span
                                            key={p}
                                            className={`text-[10px] px-1.5 py-0.5 rounded ${
                                              dark
                                                ? "bg-blue-500/15 text-blue-400"
                                                : "bg-blue-100 text-blue-700"
                                            }`}
                                          >
                                            {p}
                                          </span>
                                        ))}
                                    </div>
                                  </div>
                                ) : fdStep === "refine" && fdGeneric ? (
                                  /* FD Refine — pick manufacturer + pack from API drug */
                                  <div className="space-y-3">
                                    <div
                                      className={`flex items-center gap-2 px-2.5 py-2 rounded-lg ${
                                        dark
                                          ? "bg-gray-800"
                                          : "bg-white border border-gray-200"
                                      }`}
                                    >
                                      <Pill
                                        className={`w-3.5 h-3.5 shrink-0 ${
                                          dark
                                            ? "text-blue-400"
                                            : "text-blue-600"
                                        }`}
                                      />
                                      <span
                                        className={`text-xs font-medium flex-1 ${
                                          dark ? "text-white" : "text-gray-900"
                                        }`}
                                      >
                                        {fdGeneric}
                                      </span>
                                      <button
                                        onClick={() => {
                                          setFdStep("search");
                                          setFdSearch("");
                                          setFdGeneric(null);
                                          setFdManufacturers([]);
                                          setFdPackChoices([]);
                                          setFdSelectedDrug(null);
                                        }}
                                        className={`text-[10px] px-1.5 py-0.5 rounded cursor-pointer ${
                                          dark
                                            ? "text-blue-400 hover:bg-blue-500/10"
                                            : "text-blue-600 hover:bg-blue-50"
                                        }`}
                                      >
                                        Change
                                      </button>
                                    </div>
                                    {/* Manufacturer — multi-select from API */}
                                    <div>
                                      <div className="flex items-center justify-between mb-1.5">
                                        <span
                                          className={`text-[10px] font-medium ${
                                            dark
                                              ? "text-gray-400"
                                              : "text-gray-500"
                                          }`}
                                        >
                                          Manufacturer
                                        </span>
                                        <span
                                          className={`text-[9px] px-1 py-0.5 rounded ${
                                            dark
                                              ? "bg-gray-800 text-gray-500"
                                              : "bg-gray-100 text-gray-400"
                                          }`}
                                        >
                                          Optional
                                        </span>
                                      </div>
                                      <div className="flex gap-1.5 flex-wrap">
                                        <button
                                          onClick={() => {
                                            setFdManufacturers([]);
                                            setFdPackChoices([]);
                                          }}
                                          className={`px-2 py-1 text-[11px] font-medium rounded-md border transition-colors cursor-pointer ${
                                            fdManufacturers.length === 0
                                              ? dark
                                                ? "bg-blue-500/20 text-blue-300 border-blue-500/40"
                                                : "bg-blue-100 text-blue-700 border-blue-200"
                                              : dark
                                              ? "bg-gray-800 text-gray-400 border-gray-700"
                                              : "bg-white text-gray-500 border-gray-200"
                                          }`}
                                        >
                                          Any
                                        </button>
                                        {fdSelectedDrug &&
                                          (() => {
                                            const seen = new Set<string>();
                                            return fdSelectedDrug.manufacturer
                                              .filter((m: any) => {
                                                if (
                                                  seen.has(m.manufacturerName)
                                                )
                                                  return false;
                                                seen.add(m.manufacturerName);
                                                return true;
                                              })
                                              .map((m: any) => (
                                                <button
                                                  key={m.manufacturerName}
                                                  onClick={() => {
                                                    setFdManufacturers([
                                                      m.manufacturerName,
                                                    ]);
                                                    setFdPackChoices([]);
                                                  }}
                                                  className={`px-2 py-1 text-[11px] font-medium rounded-md border transition-colors cursor-pointer ${
                                                    fdManufacturers[0] ===
                                                    m.manufacturerName
                                                      ? dark
                                                        ? "bg-blue-500/20 text-blue-300 border-blue-500/40"
                                                        : "bg-blue-100 text-blue-700 border-blue-200"
                                                      : dark
                                                      ? "bg-gray-800 text-gray-400 border-gray-700"
                                                      : "bg-white text-gray-500 border-gray-200"
                                                  }`}
                                                >
                                                  {m.manufacturerName}
                                                </button>
                                              ));
                                          })()}
                                      </div>
                                    </div>
                                    {/* Pack Size — multi-select from API */}
                                    <div>
                                      <div className="flex items-center justify-between mb-1.5">
                                        <span
                                          className={`text-[10px] font-medium ${
                                            dark
                                              ? "text-gray-400"
                                              : "text-gray-500"
                                          }`}
                                        >
                                          Pack Size
                                        </span>
                                        <span
                                          className={`text-[9px] px-1 py-0.5 rounded ${
                                            dark
                                              ? "bg-gray-800 text-gray-500"
                                              : "bg-gray-100 text-gray-400"
                                          }`}
                                        >
                                          Optional · multi-select
                                        </span>
                                      </div>
                                      <div className="flex gap-1.5 flex-wrap">
                                        <button
                                          onClick={() => setFdPackChoices([])}
                                          className={`px-2 py-1 text-[11px] font-medium rounded-md border transition-colors cursor-pointer ${
                                            fdPackChoices.length === 0
                                              ? dark
                                                ? "bg-blue-500/20 text-blue-300 border-blue-500/40"
                                                : "bg-blue-100 text-blue-700 border-blue-200"
                                              : dark
                                              ? "bg-gray-800 text-gray-400 border-gray-700"
                                              : "bg-white text-gray-500 border-gray-200"
                                          }`}
                                        >
                                          Any
                                        </button>
                                        {fdSelectedDrug &&
                                          (() => {
                                            const seen = new Set<string>();
                                            const packs: string[] = [];
                                            fdSelectedDrug.manufacturer
                                              .filter(
                                                (m: any) =>
                                                  fdManufacturers.length ===
                                                    0 ||
                                                  fdManufacturers.includes(
                                                    m.manufacturerName,
                                                  ),
                                              )
                                              .forEach((m: any) =>
                                                m.packSizes.forEach(
                                                  (s: number) => {
                                                    const label = `${Math.round(
                                                      s,
                                                    )} ${
                                                      fdSelectedDrug.packUnit
                                                    }`;
                                                    if (!seen.has(label)) {
                                                      seen.add(label);
                                                      packs.push(label);
                                                    }
                                                  },
                                                ),
                                              );
                                            return packs.map((p) => (
                                              <button
                                                key={p}
                                                onClick={() =>
                                                  setFdPackChoices((prev) =>
                                                    prev.includes(p)
                                                      ? prev.filter(
                                                          (x) => x !== p,
                                                        )
                                                      : [...prev, p],
                                                  )
                                                }
                                                className={`px-2 py-1 text-[11px] font-medium rounded-md border transition-colors cursor-pointer ${
                                                  fdPackChoices.includes(p)
                                                    ? dark
                                                      ? "bg-blue-500/20 text-blue-300 border-blue-500/40"
                                                      : "bg-blue-100 text-blue-700 border-blue-200"
                                                    : dark
                                                    ? "bg-gray-800 text-gray-400 border-gray-700"
                                                    : "bg-white text-gray-500 border-gray-200"
                                                }`}
                                              >
                                                {p}
                                              </button>
                                            ));
                                          })()}
                                      </div>
                                    </div>
                                    {/* Confirm */}
                                    <button
                                      onClick={() => {
                                        const mfrPart =
                                          fdManufacturers.length > 0
                                            ? ` - ${fdManufacturers.join(", ")}`
                                            : "";
                                        setEnforcedProduct(
                                          `${fdGeneric}${mfrPart}`,
                                        );
                                        setEnforcedPack(
                                          fdPackChoices.length > 0
                                            ? fdPackChoices.join(", ")
                                            : "",
                                        );
                                        setFdStep("done");
                                      }}
                                      className={`w-full py-1.5 text-[11px] font-medium rounded-lg cursor-pointer transition-colors ${
                                        dark
                                          ? "bg-blue-600 text-white hover:bg-blue-500"
                                          : "bg-blue-600 text-white hover:bg-blue-500"
                                      }`}
                                    >
                                      Confirm Selection
                                    </button>
                                  </div>
                                ) : (
                                  /* FD Search — API-driven, same as Search by name */
                                  <>
                                    <div className="relative mb-2">
                                      <Search
                                        className={`absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${
                                          dark
                                            ? "text-gray-500"
                                            : "text-gray-400"
                                        }`}
                                      />
                                      <input
                                        type="text"
                                        placeholder="Search drug name..."
                                        value={fdSearch}
                                        onChange={(e) =>
                                          setFdSearch(e.target.value)
                                        }
                                        className={`w-full pl-9 pr-8 py-2 text-sm rounded-lg border outline-none transition-colors ${
                                          fdMissing
                                            ? dark
                                              ? "border-red-500/40 bg-gray-800 text-white placeholder:text-gray-600 focus:border-red-500"
                                              : "border-red-300 bg-white text-gray-900 placeholder:text-gray-400 focus:border-red-400"
                                            : dark
                                            ? "bg-gray-800 border-gray-700 text-white placeholder:text-gray-600 focus:border-blue-500"
                                            : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-blue-400"
                                        }`}
                                      />
                                      {fdSearch && (
                                        <button
                                          onClick={() => setFdSearch("")}
                                          className={`absolute right-2.5 top-1/2 -translate-y-1/2 cursor-pointer ${
                                            dark
                                              ? "text-gray-500 hover:text-gray-300"
                                              : "text-gray-400 hover:text-gray-600"
                                          }`}
                                        >
                                          <X className="w-3.5 h-3.5" />
                                        </button>
                                      )}
                                    </div>
                                    {fdApiLoading && (
                                      <p
                                        className={`text-[11px] text-center py-2 ${
                                          dark
                                            ? "text-gray-500"
                                            : "text-gray-400"
                                        }`}
                                      >
                                        Searching…
                                      </p>
                                    )}
                                    {!fdApiLoading &&
                                      fdSearch.length >= 2 &&
                                      (fdApiResults.length > 0 ? (
                                        <div
                                          className={`rounded-lg border max-h-[160px] overflow-y-auto ${
                                            dark
                                              ? "border-gray-700"
                                              : "border-gray-200"
                                          }`}
                                        >
                                          {fdApiResults.map(
                                            (drug: any, idx: number) => (
                                              <button
                                                key={
                                                  drug.prescribedItemId || idx
                                                }
                                                onClick={() => {
                                                  setFdSelectedDrug(drug);
                                                  setFdGeneric(
                                                    drug.drugName.trim(),
                                                  );
                                                  setFdManufacturers([]);
                                                  setFdPackChoices([]);
                                                  setFdStep("refine");
                                                  setFdSearch("");
                                                }}
                                                className={`w-full text-left px-3 py-2 text-xs cursor-pointer transition-colors ${
                                                  dark
                                                    ? "hover:bg-gray-800 text-gray-200"
                                                    : "hover:bg-gray-50 text-gray-800"
                                                } ${
                                                  idx > 0
                                                    ? `border-t ${
                                                        dark
                                                          ? "border-gray-800"
                                                          : "border-gray-100"
                                                      }`
                                                    : ""
                                                }`}
                                              >
                                                <span className="font-medium">
                                                  {drug.drugName}
                                                </span>
                                                <span
                                                  className={`ml-1.5 text-[10px] ${
                                                    dark
                                                      ? "text-gray-500"
                                                      : "text-gray-400"
                                                  }`}
                                                >
                                                  {drug.manufacturer?.length ||
                                                    0}{" "}
                                                  manufacturer
                                                  {drug.manufacturer?.length !==
                                                  1
                                                    ? "s"
                                                    : ""}
                                                </span>
                                              </button>
                                            ),
                                          )}
                                        </div>
                                      ) : (
                                        <p
                                          className={`text-[11px] text-center py-2 ${
                                            dark
                                              ? "text-gray-600"
                                              : "text-gray-400"
                                          }`}
                                        >
                                          No matching drugs
                                        </p>
                                      ))}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Expiry Date */}
                  <div>
                    <label
                      className={`block text-xs font-medium mb-1.5 ${
                        dark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Expiry Date
                    </label>
                    <p
                      className={`text-[10px] mb-2 ${
                        dark ? "text-gray-600" : "text-gray-400"
                      }`}
                    >
                      Set a date for this rule to automatically expire. Leave
                      empty for no expiry.
                    </p>
                    <div className="relative">
                      <CalendarClock
                        className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                          dark ? "text-gray-500" : "text-gray-400"
                        }`}
                      />
                      <input
                        type="date"
                        value={expiryDate}
                        onChange={(e) => setExpiryDate(e.target.value)}
                        min={new Date().toISOString().split("T")[0]}
                        className={`w-full pl-10 pr-10 py-2.5 text-sm rounded-xl border outline-none transition-colors ${
                          dark
                            ? "bg-gray-800 border-gray-700 text-white focus:border-purple-500"
                            : "bg-gray-50 border-gray-200 text-gray-900 focus:border-purple-400"
                        } ${
                          expiryDate
                            ? ""
                            : dark
                            ? "text-gray-600"
                            : "text-gray-400"
                        }`}
                      />
                      {expiryDate && (
                        <button
                          onClick={() => setExpiryDate("")}
                          className={`absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer ${
                            dark
                              ? "text-gray-500 hover:text-gray-300"
                              : "text-gray-400 hover:text-gray-600"
                          }`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={`flex items-center justify-between px-6 py-4 border-t shrink-0 ${
            dark ? "border-gray-800" : "border-gray-100"
          }`}
        >
          <div>
            {onDelete && (
              <button
                onClick={onDelete}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg cursor-pointer transition-colors ${
                  dark
                    ? "text-red-400 hover:bg-red-500/10"
                    : "text-red-600 hover:bg-red-50"
                }`}
              >
                <Trash2 className="w-3.5 h-3.5" />
                Remove Drug
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className={`px-4 py-2 text-xs font-medium rounded-lg cursor-pointer transition-colors ${
                dark
                  ? "text-gray-400 hover:bg-gray-800"
                  : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              Cancel
            </button>
            {step === "configure" && (
              <button
                onClick={handleSave}
                disabled={
                  !name.trim() ||
                  (actions.includes("forceDispensing") && !enforcedProduct) ||
                  assignedStores.length === 0
                }
                title={
                  actions.includes("forceDispensing") && !enforcedProduct
                    ? "Select a product for Force Dispensing"
                    : assignedStores.length === 0
                    ? "Select at least one store"
                    : ""
                }
                className={`px-4 py-2 text-xs font-medium rounded-lg cursor-pointer transition-colors ${
                  name.trim() &&
                  !(actions.includes("forceDispensing") && !enforcedProduct) &&
                  assignedStores.length > 0
                    ? dark
                      ? "bg-purple-600 text-white hover:bg-purple-500"
                      : "bg-gray-900 text-white hover:bg-gray-800"
                    : dark
                    ? "bg-gray-800 text-gray-600 cursor-not-allowed"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
              >
                {isEdit ? "Save Changes" : "Add to List"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
